'use client';

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { safeSanitize } from '@/core/utils/sanitize';
import { trackAddToCart } from '@/core/utils/analytics';
import { ShoppingCart, Play, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Sliders, Star, Check, Minus, Plus, Smartphone } from 'lucide-react';
import { Button } from '@/core/components/ui/button';
import { toast } from '@/core/utils/toast';
import Image from 'next/image';
import { BlockProps } from './types';
import { cn } from '@/core/utils/cn';
import { getAbsoluteMediaUrl, toOptimizableSrc } from '@/core/utils/media';
import { formatPrice } from '@/core/utils/currency';
import { getHoverClass } from '../blockHelpers';
import { useProductPrice } from '@/hooks/useProductPrice';
import { applyB2BTiers, b2bTierPrice, type B2BTier } from '@/core/utils/b2b-pricing';
import { B2BTierList } from '@/modules/shop/components/product/B2BTierList';
import { useTranslation } from '@/core/components/providers/TranslationProvider';
import { useSiteConfig } from '@/core/components/providers/SiteConfigProvider';
import { useProductState, type ProductPageControl } from '../ProductContext';
import { ALL_BREAKPOINTS, type Breakpoint } from '../breakpoints';
import { useVisibleBreakpoints } from '../VisibleBreakpoints';
import { Combobox } from '@/core/components/ui/combobox';
import { evaluateConfigurator, isConfiguratorComplete } from '../configurator-utils';
import { addConfigurableToCart } from '@/lib/cart';
import { useSession } from 'next-auth/react';
import { LoyaltyEarnPreview } from '@/core/components/loyalty/LoyaltyEarnPreview';
import { ProductActionIcon } from '../shared/ProductActionIcon';
import { ProductBrandMark } from '../shared/ProductBrandMark';
import { buildActionButtonStyle, buildActionButtonHoverCSS, ACTION_BUTTON_DEFAULTS } from '../shared/actionButtonStyle';
import { isNegotiationAllowedForProduct } from '@/core/utils/negotiation';
import dynamic from 'next/dynamic';

const PickPackBlock = dynamic(() => import('./PickPackBlock').then(m => m.PickPackBlock));
import { fetchAPI } from '@/core/api/client';
import { useSpecTemplates } from '@/core/hooks/useSpecTemplates';
import { LoginGateModal } from '@/modules/shop/components/negotiation/login-gate-modal';
import { WishlistHeart } from '@/modules/shop/components/wishlist/WishlistHeart';
import { SubscriptionPurchasePanel } from '@/modules/shop/components/product/subscription-purchase-panel';
import { activeSubscriptionTiers } from '@/core/types/subscription-tiers';
import { NegotiatePriceModal } from '@/modules/shop/components/negotiation/negotiate-price-modal';
import { AddToCartModal } from '@/core/components/AddToCartModal';
import { DEMO_PRODUCT } from '../demoProduct';
import { useRouter } from '@/core/i18n/navigation';
import { useLocalizeHtml } from '@/core/i18n/navigation';
import { LeaseNowButtonBlock, LeaseNowCalculatorBlock } from './LeaseNowBlocks';
import { ProductDownloadsBlock } from './ProductDownloadsBlock';
import { priceOnRequestLabel, serviceBillableArea, serviceMinQuantity, servicePricingType, usesConfigurator } from '@/core/utils/product-action';

export { DEMO_PRODUCT };

// ─── Small "DEMO" badge shown in admin on rendered blocks ────────────────────

function DemoBadge() {
    return (
        <span className="absolute top-1 right-1 z-50 text-[7px] font-black uppercase tracking-widest bg-amber-400 text-amber-900 px-1.5 py-0.5 rounded-full shadow pointer-events-none select-none">
            DEMO
        </span>
    );
}

// ─── Shared helper ───────────────────────────────────────────────────────────

function getMediaUrl(path: string) {
    // Demo/placeholder images are frontend-local public assets, not backend media —
    // they don't need absolutizing, and doing so causes an SSR/client hydration mismatch
    // (server uses FRONTEND_URL, client uses window.location.origin, which differ on
    // tenant subdomains like shop1.localhost).
    if (path.startsWith('/demo_product/')) return path;
    return toOptimizableSrc(getAbsoluteMediaUrl(path));
}

// Map thumbSize setting to pixel dimensions
const THUMB_SIZE_MAP: Record<string, number> = {
    sm: 48,
    md: 64,
    lg: 80,
    xl: 96,
};

// Map aspectRatio setting to CSS class
const ASPECT_RATIO_CLASS: Record<string, string> = {
    'square': 'aspect-square',
    '4:3': 'aspect-[4/3]',
    '3:4': 'aspect-[3/4]',
    '16:9': 'aspect-video',
};

// ─── product-name ────────────────────────────────────────────────────────────

export function ProductNameBlock({ block, currentStyles, isAdmin, product }: BlockProps) {
    const data = product || (isAdmin ? DEMO_PRODUCT : null);
    if (!data) return null;
    const level = block.settings?.level || 'h1';
    const HeaderTag = level as any;
    const defaultLevelClass = level === 'h1' ? 'text-3xl md:text-4xl font-extrabold' : level === 'h2' ? 'text-2xl md:text-3xl font-bold' : 'text-xl md:text-2xl font-bold';
    const showBestsellerBadge = block.settings?.showBestsellerBadge !== false && data.is_bestseller;
    return (
        <div className={isAdmin ? 'relative' : ''}>
            {isAdmin && <DemoBadge />}
            {showBestsellerBadge && (
                <span
                    className="inline-flex items-center gap-1 mb-2 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide"
                    style={{
                        backgroundColor: block.settings?.bestsellerBadgeBg || '#f59e0b',
                        color: block.settings?.bestsellerBadgeTextColor || '#ffffff',
                    }}
                >
                    <Star className="w-3 h-3 fill-current" />
                    {block.settings?.bestsellerBadgeText || 'Bestseller'}
                </span>
            )}
            <HeaderTag style={currentStyles} className={cn("tracking-tight leading-tight transition-all", defaultLevelClass)}>
                {data.name}
            </HeaderTag>
        </div>
    );
}

/** Tells the configurator this block is on the page — on the breakpoints it's actually shown on —
 *  so it doesn't render a second copy of the same control there (see ProductPageControl). A block
 *  hidden on every device used to count too: zojahandmade hid "Dodaj do koszyka" to use the
 *  configurator's own button, and the configurator then left its button out as well. */
function usePageControl(control: ProductPageControl, active: boolean) {
    const register = useProductState()?.registerPageControl;
    const visible = useVisibleBreakpoints();
    const visibleKey = visible.join(',');
    useEffect(
        () => (active && register && visible.length > 0 ? register(control, visible) : undefined),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [active, register, control, visibleKey],
    );
}

/** For the configurator's own copy of a control: undefined when page blocks show it on every
 *  breakpoint (leave the copy out), otherwise the classes hiding the copy where they do show it. */
function ownControlClass(
    pageControls: Partial<Record<ProductPageControl, Partial<Record<Breakpoint, number>>>>,
    control: ProductPageControl,
): string | undefined {
    const covered = ALL_BREAKPOINTS.filter((bp) => (pageControls[control]?.[bp] || 0) > 0);
    if (covered.length === ALL_BREAKPOINTS.length) return undefined;
    return covered.map((bp) => `block-hide-${bp}`).join(' ');
}

// ─── product-price ───────────────────────────────────────────────────────────

export function ProductPriceBlock({ block, currentStyles, isAdmin, product }: BlockProps) {
    const { t, currency, locale } = useTranslation();
    const productState = useProductState();
    const data = productState?.product || product || (isAdmin ? DEMO_PRODUCT : null);

    const activeVariant = productState?.selectedVariant;
    const configuratorPrice = productState?.configuratorPrice;
    const priceToUse = !data ? 0 : activeVariant
        ? activeVariant.price
        : (usesConfigurator(data) && configuratorPrice !== null && configuratorPrice !== undefined
            ? configuratorPrice
            : data.price);
    const salesPriceToUse = !data ? null : activeVariant
        ? activeVariant.sales_price
        : (usesConfigurator(data) ? null : data.sales_price);
    const lowest30 = !data ? undefined : activeVariant ? activeVariant.lowest_price_30_days : data.lowest_price_30_days;
    // Progi ilościowe zalogowanej firmy (D9): cena jednostkowa dla ilości z bloku ilości — ta sama,
    // którą policzy koszyk i zamówienie. Bez konfiguratora (tam cena zależy od opcji).
    const b2bTiers: B2BTier[] = !data || usesConfigurator(data) ? [] : ((activeVariant || data).b2b_tiers || []);
    const tierPriceToUse = b2bTiers.length && priceToUse != null ? b2bTierPrice(priceToUse, b2bTiers, productState?.quantity || 1) : priceToUse;

    // useProductPrice must stay above the `!data` early return below — a hook placed after a
    // conditional return fires on some renders and not others (whenever `data` is falsy), which
    // React rejects with error #310 the moment `data` becomes truthy on a later render.
    const { displayPrice: unitPrice, displaySalesPrice: unitSalesPrice, isNetPrice } = useProductPrice(
        tierPriceToUse,
        salesPriceToUse,
        data?.tax_rate ?? 23
    );
    usePageControl('price', !isAdmin && !!data);

    if (!data) return null;

    // Reflects whatever a product-quantity block on the page has picked (1 when
    // there isn't one, so this is a no-op for pages without that block). The omnibus
    // "lowest price in 30 days" reference stays per-unit — it's a single-item legal
    // comparison price, not a total.
    // A plan is billed once per cycle, a flat-rate service once whatever the count, and a per-kg
    // service shows its per-kg price (the quantity is only the *estimated* weight) — so the picker
    // must not multiply the shown price for any of them.
    const isPlan = data.product_type === 'subscription_plan';
    const servicePricingType = data.product_type === 'service' ? (data.service_config?.pricing_type || 'unit') : null;
    const qty = isPlan || servicePricingType === 'flat' || servicePricingType === 'weight' || servicePricingType === 'area' ? 1 : (productState?.quantity || 1);
    const price = unitPrice !== null && unitPrice !== undefined ? unitPrice * qty : unitPrice;
    const salesPrice = unitSalesPrice !== null && unitSalesPrice !== undefined ? unitSalesPrice * qty : unitSalesPrice;

    const settings = block.settings || {};
    const layout: 'row' | 'column' = settings.layout ?? 'row';
    const salePriceFirst: boolean = settings.salePriceFirst !== false;
    const showOmnibus: boolean = settings.showOmnibus !== false;
    // Off unless switched on — templates saved before it existed render exactly as before.
    const brandEl = settings.showBrand === true && data.brand ? (
        <ProductBrandMark
            brand={data.brand}
            display={settings.brandDisplay ?? 'logo_or_name'}
            logoSize={settings.brandLogoSize ?? 28}
            color={currentStyles.brandColor}
            fontSize={currentStyles.brandFontSize ?? 14}
        />
    ) : null;

    const hasSale = salesPrice !== null && salesPrice !== undefined;

    const salePriceStyle: React.CSSProperties = {
        color: currentStyles.salePriceColor || '#dc2626',
        fontSize: currentStyles.salePriceFontSize ? `${currentStyles.salePriceFontSize}px` : '2.25rem',
        fontWeight: 'bold',
        backgroundColor: currentStyles.salePriceBg || undefined,
        padding: currentStyles.salePriceBg
            ? `${currentStyles.salePricePaddingY ?? 4}px ${currentStyles.salePricePaddingX ?? 10}px`
            : undefined,
        borderRadius: currentStyles.salePriceBg ? `${currentStyles.salePriceBorderRadius ?? 8}px` : undefined,
        borderWidth: currentStyles.salePriceBorderWidth ? `${currentStyles.salePriceBorderWidth}px` : undefined,
        borderStyle: currentStyles.salePriceBorderWidth ? 'solid' : undefined,
        borderColor: currentStyles.salePriceBorderColor || undefined,
        display: currentStyles.salePriceBg ? 'inline-block' : undefined,
    };
    const regularPriceStyle: React.CSSProperties = {
        color: hasSale ? (currentStyles.regularPriceColor || '#94a3b8') : (currentStyles.regularPriceColor || 'inherit'),
        fontSize: currentStyles.regularPriceFontSize
            ? `${currentStyles.regularPriceFontSize}px`
            : hasSale ? '1.5rem' : '2.25rem',
        fontWeight: hasSale ? 'normal' : 'bold',
        textDecoration: hasSale ? 'line-through' : 'none',
        backgroundColor: currentStyles.regularPriceBg || undefined,
        padding: currentStyles.regularPriceBg
            ? `${currentStyles.regularPricePaddingY ?? 4}px ${currentStyles.regularPricePaddingX ?? 10}px`
            : undefined,
        borderRadius: currentStyles.regularPriceBg ? `${currentStyles.regularPriceBorderRadius ?? 8}px` : undefined,
        borderWidth: currentStyles.regularPriceBorderWidth ? `${currentStyles.regularPriceBorderWidth}px` : undefined,
        borderStyle: currentStyles.regularPriceBorderWidth ? 'solid' : undefined,
        borderColor: currentStyles.regularPriceBorderColor || undefined,
        display: currentStyles.regularPriceBg ? 'inline-block' : undefined,
    };

    const salePriceEl = hasSale ? (
        <p key="sale" style={salePriceStyle}>{formatPrice(salesPrice, currency, locale)}</p>
    ) : null;
    const regularPriceEl = (
        <p key="regular" style={regularPriceStyle}>{formatPrice(price, currency, locale)}</p>
    );

    const priceElements = salePriceFirst
        ? [salePriceEl, regularPriceEl].filter(Boolean)
        : [regularPriceEl, salePriceEl].filter(Boolean);

    if (data.price_on_request) {
        // "Cena na zapytanie": the label in the regular-price style — no amount, Omnibus or loyalty lines.
        return (
            <div className={isAdmin ? 'relative' : ''}>
                {isAdmin && <DemoBadge />}
                <p
                    className="product-price-on-request"
                    style={{
                        ...regularPriceStyle,
                        color: currentStyles.regularPriceColor || 'inherit',
                        textDecoration: 'none',
                        fontWeight: 'bold',
                        fontSize: currentStyles.regularPriceFontSize ? `${currentStyles.regularPriceFontSize}px` : '1.5rem',
                    }}
                >
                    {priceOnRequestLabel(data, t)}
                </p>
            </div>
        );
    }

    const unitSuffix = isPlan
        ? (data.service_config?.billing_period === 'weekly' ? t('product.per_week', '/ tydz.') : t('product.per_month', '/ mies.'))
        : servicePricingType === 'weight' ? t('product.per_kg', '/ kg')
        : servicePricingType === 'area' ? t('product.per_m2', '/ m²') : null;
    if (unitSuffix) {
        priceElements.push(<span key="unit" className="text-sm font-semibold text-slate-500">{unitSuffix}</span>);
    }

    return (
        <div className={isAdmin ? 'relative' : ''}>
            {isAdmin && <DemoBadge />}
            <div className="space-y-1">
                <div
                    className={cn('flex', layout === 'column' ? 'flex-col items-start' : 'flex-row items-baseline', brandEl && layout !== 'column' && 'flex-wrap')}
                    style={{ gap: `${currentStyles.gap ?? settings.gap ?? 12}px` }}
                >
                    {priceElements}
                    {brandEl && <span className={cn('flex', layout === 'column' ? '' : 'ml-auto self-center')}>{brandEl}</span>}
                </div>
                {hasSale && showOmnibus && lowest30 !== null && lowest30 !== undefined && (
                    <p className="text-xs text-muted-foreground italic bg-slate-50 p-2 rounded-lg border border-slate-100 inline-block">
                        {t('product.lowest_price_30')} <span className="font-bold">{formatPrice(lowest30, currency, locale)}</span>
                    </p>
                )}
                {isNetPrice && (
                    <p className="text-xs text-blue-600 font-semibold">{t('product.net_price_b2b', 'Cena netto (B2B)')}</p>
                )}
                {!isAdmin && b2bTiers.length > 0 && (
                    <B2BTierList tiers={b2bTiers} taxRate={data.tax_rate ?? 23} net={isNetPrice} />
                )}
                {!isAdmin && settings.showLoyaltyPreview !== false && (
                    <LoyaltyEarnPreview
                        amount={hasSale ? salesPrice! : (price ?? 0)}
                        label={settings.loyaltyPreviewLabel || 'Otrzymasz za ten zakup'}
                        style={{
                            color: currentStyles.loyaltyPreviewTextColor || undefined,
                            backgroundColor: currentStyles.loyaltyPreviewBgColor || undefined,
                            borderColor: currentStyles.loyaltyPreviewBgColor ? 'transparent' : undefined,
                            fontSize: currentStyles.loyaltyPreviewFontSize ? `${currentStyles.loyaltyPreviewFontSize}px` : undefined,
                        }}
                    />
                )}
            </div>
        </div>
    );
}

// ─── service measure (per-m² services) ───────────────────────────────────────

/** Width × length of one piece of a per-m² service (dywan, wykładzina) — kept in the product
 *  context, read by the add-to-cart button and the configurator; the server prices the line from
 *  the same numbers (create_order). */
function ServiceMeasureFields({ product }: { product: any }) {
    const { t, locale } = useTranslation();
    const productState = useProductState();
    const setServiceMeasure = productState?.setServiceMeasure;
    const [width, setWidth] = useState(() => String(productState?.serviceMeasure?.width_cm ?? ''));
    const [length, setLength] = useState(() => String(productState?.serviceMeasure?.length_cm ?? ''));

    const w = parseInt(width, 10);
    const l = parseInt(length, 10);
    const valid = w > 0 && l > 0 && w <= 10000 && l <= 10000;
    useEffect(() => {
        setServiceMeasure?.(valid ? { width_cm: w, length_cm: l } : null);
    }, [valid, w, l, setServiceMeasure]);

    if (!productState) return null;
    const measured = valid ? Math.round((w * l) / 100) / 100 : null;
    const billable = valid ? serviceBillableArea(product, { width_cm: w, length_cm: l }) : null;
    const fmt = (n: number) => n.toLocaleString(locale, { maximumFractionDigits: 2 });

    return (
        <div className="w-full space-y-2">
            <p className="text-sm font-medium text-slate-600">{t('product.measure_label', 'Wymiary jednej sztuki (cm)')}</p>
            <div className="flex flex-wrap items-center gap-2">
                <input
                    type="number" inputMode="numeric" min={1} value={width} onChange={(e) => setWidth(e.target.value)}
                    placeholder={t('product.measure_width', 'Szerokość')} aria-label={t('product.measure_width', 'Szerokość')}
                    className="w-28 h-10 px-3 border border-slate-200 rounded-lg text-sm font-semibold"
                />
                <span className="text-slate-400">×</span>
                <input
                    type="number" inputMode="numeric" min={1} value={length} onChange={(e) => setLength(e.target.value)}
                    placeholder={t('product.measure_length', 'Długość')} aria-label={t('product.measure_length', 'Długość')}
                    className="w-28 h-10 px-3 border border-slate-200 rounded-lg text-sm font-semibold"
                />
                {measured !== null && (
                    <span className="text-sm font-semibold text-slate-700">= {fmt(measured)} m²</span>
                )}
            </div>
            {billable !== null && measured !== null && billable > measured && (
                <p className="text-xs text-slate-500">
                    {t('product.measure_minimum', 'Minimalna powierzchnia to {n} m² — tyle policzymy za tę sztukę.').replace('{n}', fmt(billable))}
                </p>
            )}
        </div>
    );
}

/** Quantity of a service right by its add-to-cart button, for templates without a product-quantity
 *  block — a per-kg service had no way to state the weight there (4 kg went in silently). Weight for
 *  per-kg, pieces for per-m² (one size), count otherwise; never below the minimum load. */
function InlineServiceQuantity({ product }: { product: any }) {
    const { t } = useTranslation();
    const productState = useProductState();
    if (!productState) return null;
    const pricing = servicePricingType(product);
    const min = serviceMinQuantity(product);
    const value = Math.max(min, productState.quantity || 1);
    const set = (n: number) => productState.setQuantity(Math.max(min, Math.min(999, n)));
    const label = pricing === 'weight' ? t('product.quantity_weight', 'Szacowana waga (kg)')
        : pricing === 'area' ? t('product.quantity_pieces', 'Liczba sztuk tego rozmiaru')
        : t('product.quantity', 'Ilość');
    return (
        <div className="w-full space-y-1.5">
            <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm font-medium text-slate-600">{label}</span>
                <div className="flex items-center rounded-full border border-slate-200 bg-white">
                    <button type="button" onClick={() => set(value - 1)} disabled={value <= min}
                        className="flex h-10 w-10 items-center justify-center text-slate-600 disabled:opacity-30" aria-label={t('product.quantity_decrease', 'Zmniejsz ilość')}>
                        <Minus className="h-4 w-4" />
                    </button>
                    <span className="min-w-10 text-center font-bold tabular-nums">{value}</span>
                    <button type="button" onClick={() => set(value + 1)}
                        className="flex h-10 w-10 items-center justify-center text-slate-600" aria-label={t('product.quantity_increase', 'Zwiększ ilość')}>
                        <Plus className="h-4 w-4" />
                    </button>
                </div>
            </div>
            {min > 1 && (
                <p className="text-xs text-slate-500">
                    {(pricing === 'weight'
                        ? t('product.min_load_kg', 'Minimalny wsad: {n} kg — mniejszą ilość rozliczamy jak {n} kg.')
                        : t('product.min_load_units', 'Minimalne zamówienie: {n} szt.')).replaceAll('{n}', String(min))}
                </p>
            )}
        </div>
    );
}

// ─── product-quantity ────────────────────────────────────────────────────────
// Shared with product-price (multiplies the shown price) and product-add-to-cart
// (adds/negotiates this many units) via ProductContext's `quantity`/`setQuantity` —
// merchants can place this block anywhere on the page independently of those two.

export function ProductQuantityBlock({ block, currentStyles, isAdmin, product }: BlockProps) {
    const { t } = useTranslation();
    const productState = useProductState();
    const data = productState?.product || product || (isAdmin ? DEMO_PRODUCT : null);
    const hidden = !data || data.is_one_off || data.product_type === 'subscription_plan'
        || (data.product_type === 'service' && data.service_config?.pricing_type === 'flat');
    usePageControl('quantity', !isAdmin && !hidden);
    if (!data) return null;

    // One-off products can only ever be bought once — a quantity stepper doesn't
    // apply, so it's hidden rather than shown-but-disabled (less confusing).
    if (data.is_one_off) return null;
    // Same for a subscription plan and a flat-rate service: one purchase, no count to pick.
    if (data.product_type === 'subscription_plan'
        || (data.product_type === 'service' && data.service_config?.pricing_type === 'flat')) return null;

    const activeVariant = productState?.selectedVariant;
    const settings = block.settings || {};
    const isWeightService = data.product_type === 'service' && data.service_config?.pricing_type === 'weight';
    const label: string = settings.label ?? (isWeightService ? t('product.quantity_weight', 'Szacowana waga (kg)') : t('product.quantity', 'Ilość'));
    const showLabel: boolean = settings.showLabel !== false;
    // A service's minimum load ("min. 4 kg") is a floor no block setting can go under.
    const minLoad = serviceMinQuantity(data);
    const min: number = Math.max(minLoad, settings.min ?? 1);
    const step: number = settings.step ?? 1;
    // Cap to available stock — same rule cart/checkout already enforce, just
    // surfaced earlier so a merchant can't request more than can actually ship.
    const stockLimit = activeVariant ? activeVariant.stock : data.stock;
    // A service isn't a stocked unit either (create_order never decrements it).
    const isDigital = data.requires_shipping === false || data.product_type === 'service';
    const max: number = settings.max ?? (!isDigital && typeof stockLimit === 'number' && stockLimit > 0 ? stockLimit : 99);

    const quantity = productState?.quantity ?? 1;
    const setQuantity = productState?.setQuantity;

    const handleChange = (next: number) => {
        setQuantity?.(Math.max(min, Math.min(max, next)));
    };

    const bgColor = currentStyles.bgColor || '#f8fafc';
    const borderColor = currentStyles.borderColor || '#e2e8f0';
    const borderWidth = currentStyles.borderWidth ?? 1;
    const textColor = currentStyles.textColor || '#0f172a';
    const buttonColor = currentStyles.buttonColor || '#64748b';
    const borderRadius = currentStyles.borderRadius ?? 999;
    const size = currentStyles.size ?? 40;

    return (
        <div className={cn('flex flex-wrap items-center gap-3', isAdmin && 'relative')}>
            {isAdmin && <DemoBadge />}
            {showLabel && (
                <span className="text-sm font-medium" style={{ color: currentStyles.labelColor || '#64748b' }}>
                    {label}
                </span>
            )}
            <div
                className="flex items-center"
                style={{
                    backgroundColor: bgColor,
                    borderRadius: `${borderRadius}px`,
                    borderWidth: `${borderWidth}px`,
                    borderStyle: 'solid',
                    borderColor,
                }}
            >
                <button
                    type="button"
                    onClick={() => handleChange(quantity - step)}
                    disabled={!isAdmin && quantity <= min}
                    className="flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed transition-opacity"
                    style={{ width: size, height: size, color: buttonColor }}
                    aria-label={t('product.quantity_decrease', 'Zmniejsz ilość')}
                >
                    <Minus className="w-4 h-4" />
                </button>
                <span
                    className="flex items-center justify-center font-bold tabular-nums select-none"
                    style={{
                        minWidth: size,
                        height: size,
                        color: textColor,
                        fontSize: currentStyles.fontSize ? `${currentStyles.fontSize}px` : undefined,
                    }}
                >
                    {quantity}
                </span>
                <button
                    type="button"
                    onClick={() => handleChange(quantity + step)}
                    disabled={!isAdmin && quantity >= max}
                    className="flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed transition-opacity"
                    style={{ width: size, height: size, color: buttonColor }}
                    aria-label={t('product.quantity_increase', 'Zwiększ ilość')}
                >
                    <Plus className="w-4 h-4" />
                </button>
            </div>
            {minLoad > 1 && (
                <span className="text-xs text-slate-500">
                    {(isWeightService
                        ? t('product.min_load_kg', 'Minimalny wsad: {n} kg — mniejszą ilość rozliczamy jak {n} kg.')
                        : t('product.min_load_units', 'Minimalne zamówienie: {n} szt.')).replaceAll('{n}', String(minLoad))}
                </span>
            )}
        </div>
    );
}

// ─── product-gallery ─────────────────────────────────────────────────────────

/** Gallery display order: the main image first, then by `order`. */
function sortGalleryImages(images: any[]) {
    return [...images].sort((a: any, b: any) => {
        if (a.is_main && !b.is_main) return -1;
        if (!a.is_main && b.is_main) return 1;
        return (a.order || 0) - (b.order || 0);
    });
}

export function ProductGalleryBlock({ block, currentStyles, isAdmin, product }: BlockProps) {
    const { t } = useTranslation();
    const productState = useProductState();
    const data = productState?.product || product || (isAdmin ? DEMO_PRODUCT : null);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [zoomOpen, setZoomOpen] = useState(false);
    const [isHovering, setIsHovering] = useState(false);
    const sliderRef = useRef<HTMLDivElement>(null);

    const settings = block.settings || {};
    const layoutMode: 'thumbnails' | 'single' | 'hover' | 'slider' = settings.layoutMode ?? 'thumbnails';
    const autoPlay: boolean = settings.autoPlay === true;
    const autoPlayInterval: number = Math.max(1, settings.autoPlayInterval ?? 3);
    const zoomEnabled: boolean = settings.zoomEnabled !== false;
    const thumbPosition: 'bottom' | 'left' | 'right' = settings.thumbPosition ?? 'bottom';

    // Autoplay only makes sense where more than one image is ever visible at once
    // (cycling the main photo in 'thumbnails' mode, or advancing 'slider' slides) —
    // 'single' shows exactly one image and 'hover' is a deliberate two-state peek,
    // so a timer cycling images there would fight the merchant's own interaction.
    useEffect(() => {
        if (!autoPlay || !data) return;
        if (layoutMode !== 'thumbnails' && layoutMode !== 'slider') return;
        const images = data.images || [];
        if (images.length < 2) return;
        const timer = setInterval(() => {
            setSelectedIndex((prev) => (prev + 1) % images.length);
        }, autoPlayInterval * 1000);
        return () => clearInterval(timer);
    }, [autoPlay, autoPlayInterval, data?.images?.length, layoutMode]);

    // A variant with its own image (image_path) swaps the gallery's active
    // image to match — e.g. picking "Czerwony" shows the red product photo.
    // Only jumps automatically; the merchant can still click other thumbnails
    // to look around, same as before.
    // The index is looked up in the same main-first order the gallery renders — looking it up in
    // the raw data.images showed the wrong photo whenever the main image wasn't first there.
    const selectedVariantImagePath = productState?.selectedVariant?.image_path;
    useEffect(() => {
        if (!selectedVariantImagePath || !data?.images) return;
        const idx = sortGalleryImages(data.images).findIndex((img: any) => img.path === selectedVariantImagePath);
        if (idx !== -1) setSelectedIndex(idx);
    }, [selectedVariantImagePath, data?.images]);

    if (!data) return null;
    const aspectRatioClass = ASPECT_RATIO_CLASS[settings.aspectRatio ?? 'square'] ?? 'aspect-square';
    const mainBorderRadius = currentStyles.mainBorderRadius ?? settings.mainBorderRadius ?? 12;
    const mainBorderWidth = currentStyles.mainBorderWidth ?? settings.mainBorderWidth ?? 1;
    const mainBorderColor = settings.mainBorderColor || '#e2e8f0';
    const thumbSizePx = THUMB_SIZE_MAP[currentStyles.thumbSize ?? settings.thumbSize ?? 'md'] ?? 64;
    const thumbBorderRadius = currentStyles.thumbBorderRadius ?? settings.thumbBorderRadius ?? 8;
    const transitionDuration = settings.transitionDuration ?? '300';

    const images = sortGalleryImages(data.images || []);

    // Resolve hover image:
    // If hoverImageSource === 'custom' and hoverImageIndex is set, pick that 1-based index (converted to 0-based).
    // Otherwise ('auto'): prioritize image with is_hover_image === true (and not the main image if possible),
    // falling back to index 1 (second image) if available, or 0.
    const getHoverImageIndex = () => {
        if (images.length <= 1) return 0;
        if (settings.hoverImageSource === 'custom' && typeof settings.hoverImageIndex === 'number') {
            return Math.max(0, Math.min(images.length - 1, settings.hoverImageIndex - 1));
        }
        const hoverExplicitIndex = images.findIndex((img: any) => img.is_hover_image && !img.is_main);
        if (hoverExplicitIndex !== -1) return hoverExplicitIndex;
        const anyHoverIndex = images.findIndex((img: any) => img.is_hover_image);
        if (anyHoverIndex !== -1) return anyHoverIndex;
        return 1;
    };

    const hoverIndex = getHoverImageIndex();
    const isHoverActive = layoutMode === 'hover' && isHovering && images.length > 1;
    const displayIndex = isHoverActive ? hoverIndex : selectedIndex;
    const selectedMedia = images[displayIndex] || null;
    const primaryMedia = images[selectedIndex] || null;
    const hoverMedia = (layoutMode === 'hover' && images.length > 1) ? images[hoverIndex] : null;

    const mainImageStyle: React.CSSProperties = {
        borderRadius: `${mainBorderRadius}px`,
        borderWidth: mainBorderWidth > 0 ? `${mainBorderWidth}px` : 0,
        borderColor: mainBorderWidth > 0 ? mainBorderColor : 'transparent',
        borderStyle: mainBorderWidth > 0 ? 'solid' : 'none',
    };

    const transitionClass = transitionDuration === '0'
        ? ''
        : `transition-transform duration-${transitionDuration}`;

    const maxH = currentStyles?.maxHeight;
    const mainContainerStyle: React.CSSProperties = {
        ...mainImageStyle,
        ...(maxH ? { maxHeight: typeof maxH === 'number' ? `${maxH}px` : maxH } : {}),
    };

    const thumbStrip = layoutMode === 'thumbnails' && images.length > 1 && (
        <div
            className={cn(
                'flex gap-2 no-scrollbar',
                thumbPosition === 'bottom' ? 'overflow-x-auto pb-1' : 'flex-col overflow-y-auto pr-1'
            )}
        >
            {images.map((img: any, idx: number) => (
                <button
                    key={img.id}
                    onClick={() => setSelectedIndex(idx)}
                    className={`flex-shrink-0 overflow-hidden border-2 transition-all relative ${
                        idx === selectedIndex
                            ? 'border-primary ring-2 ring-primary/30'
                            : 'border-slate-200 hover:border-slate-400'
                    }`}
                    style={{
                        width: `${thumbSizePx}px`,
                        height: `${thumbSizePx}px`,
                        borderRadius: `${thumbBorderRadius}px`,
                    }}
                >
                    {img.media_type === 'video' ? (
                        <div className="w-full h-full bg-gray-900 flex items-center justify-center">
                            <Play className="w-4 h-4 text-white/70" />
                        </div>
                    ) : (
                        <Image
                            src={getMediaUrl(img.path)}
                            alt={img.alt_text || `${data.name} ${idx + 1}`}
                            fill
                            sizes={`${thumbSizePx}px`}
                            className="object-cover"
                        />
                    )}
                </button>
            ))}
        </div>
    );

    const canZoom = zoomEnabled && selectedMedia && selectedMedia.media_type !== 'video' && layoutMode !== 'slider';

    const goToSlide = (idx: number) => {
        setSelectedIndex(idx);
        const el = sliderRef.current;
        const target = el?.children[idx] as HTMLElement | undefined;
        target?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
    };

    return (
        <div className={isAdmin ? 'relative' : ''}>
            {isAdmin && <DemoBadge />}
            {layoutMode === 'slider' ? (
                <div className="relative">
                    <WishlistHeart productId={data.id} variant="gallery" preview={isAdmin} />
                    <div
                        ref={sliderRef}
                        onScroll={(e) => {
                            const el = e.currentTarget;
                            if (el.clientWidth > 0) {
                                const idx = Math.round(el.scrollLeft / el.clientWidth);
                                if (idx !== selectedIndex) setSelectedIndex(idx);
                            }
                        }}
                        className="flex overflow-x-auto snap-x snap-mandatory scroll-smooth no-scrollbar"
                    >
                        {images.length > 0 ? images.map((img: any, idx: number) => (
                            <div
                                key={img.id}
                                className={`${aspectRatioClass} bg-gray-100 flex items-center justify-center shadow-sm relative shrink-0 w-full snap-start overflow-hidden${(zoomEnabled && img.media_type !== 'video') ? ' cursor-zoom-in' : ''}`}
                                style={mainContainerStyle}
                                onClick={() => {
                                    if (zoomEnabled && img.media_type !== 'video') {
                                        setSelectedIndex(idx);
                                        setZoomOpen(true);
                                    }
                                }}
                            >
                                {img.media_type === 'video' ? (
                                    <video src={getMediaUrl(img.path)} controls className="w-full h-full object-contain bg-black" />
                                ) : (
                                    <Image
                                        src={getMediaUrl(img.path)}
                                        alt={img.alt_text || `${data.name} ${idx + 1}`}
                                        fill
                                        priority={idx === 0}
                                        fetchPriority={idx === 0 ? 'high' : undefined}
                                        sizes="(max-width: 768px) 100vw, 50vw"
                                        className="object-contain"
                                    />
                                )}
                            </div>
                        )) : (
                            <div className={`${aspectRatioClass} bg-gray-100 flex items-center justify-center w-full shrink-0`} style={mainContainerStyle}>
                                <span className="text-4xl text-gray-300">{t('product.no_photo', 'Brak zdjęcia')}</span>
                            </div>
                        )}
                    </div>

                    {images.length > 1 && (
                        <>
                            <button
                                type="button"
                                onClick={() => goToSlide(Math.max(0, selectedIndex - 1))}
                                disabled={selectedIndex === 0}
                                className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 shadow flex items-center justify-center disabled:opacity-30 hover:bg-white transition-opacity"
                                aria-label={t('product.gallery_prev', 'Poprzednie zdjęcie')}
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                            <button
                                type="button"
                                onClick={() => goToSlide(Math.min(images.length - 1, selectedIndex + 1))}
                                disabled={selectedIndex === images.length - 1}
                                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 shadow flex items-center justify-center disabled:opacity-30 hover:bg-white transition-opacity"
                                aria-label={t('product.gallery_next', 'Następne zdjęcie')}
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                            <div className="flex items-center justify-center gap-1.5 mt-2.5">
                                {images.map((img: any, idx: number) => (
                                    <button
                                        key={img.id}
                                        type="button"
                                        onClick={() => goToSlide(idx)}
                                        aria-label={t('product.gallery_goto', 'Przejdź do zdjęcia {n}').replace('{n}', String(idx + 1))}
                                        className={`h-1.5 rounded-full transition-all ${idx === selectedIndex ? 'w-5 bg-primary' : 'w-1.5 bg-gray-300 hover:bg-gray-400'}`}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </div>
            ) : (
            <div className={cn('flex gap-3', thumbPosition === 'bottom' ? 'flex-col' : thumbPosition === 'left' ? 'flex-row-reverse' : 'flex-row')}>
                <div
                    className={`${aspectRatioClass} bg-gray-100 overflow-hidden flex items-center justify-center shadow-sm relative flex-1 min-w-0${canZoom ? ' cursor-zoom-in' : ''}`}
                    style={mainContainerStyle}
                    onClick={() => canZoom && setZoomOpen(true)}
                    onMouseEnter={() => layoutMode === 'hover' && setIsHovering(true)}
                    onMouseLeave={() => layoutMode === 'hover' && setIsHovering(false)}
                >
                    <WishlistHeart productId={data.id} variant="gallery" preview={isAdmin} />
                    {selectedMedia ? (
                        selectedMedia.media_type === 'video' ? (
                            <video
                                key={selectedMedia.id}
                                src={getMediaUrl(selectedMedia.path)}
                                controls
                                className="w-full h-full object-contain bg-black"
                            />
                        ) : (
                            <>
                                <Image
                                    src={getMediaUrl(primaryMedia?.path || selectedMedia.path)}
                                    alt={primaryMedia?.alt_text || selectedMedia?.alt_text || data.name}
                                    fill
                                    priority
                                    fetchPriority="high"
                                    sizes="(max-width: 768px) 100vw, 50vw"
                                    className={cn(
                                        "object-contain transition-all duration-300",
                                        layoutMode === 'hover' && hoverMedia && hoverMedia.path !== primaryMedia?.path && isHovering ? "opacity-0 scale-105" : "opacity-100",
                                        layoutMode !== 'hover' && "hover:scale-105",
                                        transitionClass
                                    )}
                                />
                                {layoutMode === 'hover' && hoverMedia && hoverMedia.media_type !== 'video' && hoverMedia.path !== primaryMedia?.path && (
                                    <Image
                                        src={getMediaUrl(hoverMedia.path)}
                                        alt={hoverMedia?.alt_text || `${data.name} - widok 2`}
                                        fill
                                        priority
                                        sizes="(max-width: 768px) 100vw, 50vw"
                                        className={cn(
                                            "object-contain transition-all duration-300 absolute inset-0",
                                            isHovering ? "opacity-100 scale-105" : "opacity-0 pointer-events-none",
                                            transitionClass
                                        )}
                                    />
                                )}
                            </>
                        )
                    ) : (
                        <span className="text-4xl text-gray-300">Brak zdjęcia</span>
                    )}
                </div>

                {thumbStrip}
            </div>
            )}

            {zoomOpen && selectedMedia && (
                <div
                    className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-6 cursor-zoom-out"
                    onClick={() => setZoomOpen(false)}
                >
                    <button
                        type="button"
                        onClick={() => setZoomOpen(false)}
                        className="absolute top-4 right-4 text-white/80 hover:text-white text-3xl leading-none"
                        aria-label="Zamknij"
                    >
                        &times;
                    </button>
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                        src={getMediaUrl(selectedMedia.path)}
                        alt={selectedMedia?.alt_text || data.name}
                        className="max-w-full max-h-full object-contain"
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )}
        </div>
    );
}

// ─── product-description-short ───────────────────────────────────────────────

export function ProductDescriptionShortBlock({ block, currentStyles, isAdmin, product }: BlockProps) {
  const localizeHtml = useLocalizeHtml();
    const data = product || (isAdmin ? DEMO_PRODUCT : null);
    if (!data?.short_description) return null;
    return (
        <div className={isAdmin ? 'relative' : ''}>
            {isAdmin && <DemoBadge />}
            <div 
                style={currentStyles} 
                className="leading-relaxed prose prose-slate max-w-none
                    prose-h1:text-[1.5em] prose-h1:font-bold prose-h1:mb-3 prose-h1:mt-6 prose-h1:text-inherit
                    prose-h2:text-[1.25em] prose-h2:font-bold prose-h2:mb-3 prose-h2:mt-5 prose-h2:text-inherit
                    prose-h3:text-[1.1em] prose-h3:font-bold prose-h3:mb-2 prose-h3:mt-4 prose-h3:text-inherit
                    prose-p:mb-4 prose-p:leading-relaxed prose-p:text-inherit
                    prose-a:text-blue-600 prose-a:underline hover:prose-a:text-blue-800
                    prose-ul:list-disc prose-ul:pl-5 prose-ul:mb-4 prose-ul:text-inherit
                    prose-ol:list-decimal prose-ol:pl-5 prose-ol:mb-4 prose-ol:text-inherit
                    prose-li:mb-1.5 prose-li:text-inherit
                    prose-table:w-full prose-table:my-4 prose-table:border-collapse
                    prose-th:border prose-th:border-slate-200 prose-th:bg-slate-50 prose-th:p-2.5 prose-th:text-left prose-th:font-semibold
                    prose-td:border prose-td:border-slate-200 prose-td:p-2.5
                    [&_iframe]:w-full [&_iframe]:h-auto [&_iframe]:aspect-video [&_iframe]:rounded-xl [&_iframe]:my-6 [&_iframe]:border-0"
                dangerouslySetInnerHTML={{ __html: localizeHtml(safeSanitize(data.short_description || '')) }}
            />
        </div>
    );
}

// ─── product-feature-bullets ─────────────────────────────────────────────────
// Short benefit points ("Bez BPA", "Od 3 lat") pulled from product data, shown as their
// own block above the price — separate from the free-form short/long description.

export function ProductFeatureBulletsBlock({ block, currentStyles, isAdmin, product }: BlockProps) {
    const data = product || (isAdmin ? DEMO_PRODUCT : null);
    const bullets: string[] = data?.feature_bullets || (isAdmin ? ['Bez BPA', 'Od 3 lat', 'Wyprodukowano w UE'] : []);
    if (!bullets.length) return null;
    const iconColor = block.settings?.iconColor || currentStyles.iconColor || '#16a34a';
    return (
        <div className={isAdmin ? 'relative' : ''}>
            {isAdmin && !data?.feature_bullets && <DemoBadge />}
            <ul className="space-y-1.5" style={currentStyles}>
                {bullets.map((bullet, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm font-medium">
                        <Check className="w-4 h-4 shrink-0" style={{ color: iconColor }} />
                        <span>{bullet}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

// ─── product-description-long ────────────────────────────────────────────────

export function ProductDescriptionLongBlock({ block, currentStyles, isAdmin, product }: BlockProps) {
  const localizeHtml = useLocalizeHtml();
    const data = product || (isAdmin ? DEMO_PRODUCT : null);
    if (!data?.description) return null;
    return (
        <div className={isAdmin ? 'relative' : ''}>
            {isAdmin && <DemoBadge />}
            <div 
                style={currentStyles} 
                className="prose prose-slate max-w-none
                    prose-h1:text-[1.5em] prose-h1:font-bold prose-h1:mb-4 prose-h1:mt-8 prose-h1:text-inherit
                    prose-h2:text-[1.25em] prose-h2:font-bold prose-h2:mb-3 prose-h2:mt-6 prose-h2:text-inherit
                    prose-h3:text-[1.1em] prose-h3:font-bold prose-h3:mb-2.5 prose-h3:mt-5 prose-h3:text-inherit
                    prose-p:mb-4 prose-p:leading-relaxed prose-p:text-inherit
                    prose-a:text-blue-600 prose-a:underline hover:prose-a:text-blue-800
                    prose-ul:list-disc prose-ul:pl-5 prose-ul:mb-4 prose-ul:text-inherit
                    prose-ol:list-decimal prose-ol:pl-5 prose-ol:mb-4 prose-ol:text-inherit
                    prose-li:mb-1.5 prose-li:text-inherit
                    prose-table:w-full prose-table:my-6 prose-table:border-collapse
                    prose-th:border prose-th:border-slate-200 prose-th:bg-slate-50 prose-th:p-2.5 prose-th:text-left prose-th:font-semibold
                    prose-td:border prose-td:border-slate-200 prose-td:p-2.5
                    [&_iframe]:w-full [&_iframe]:h-auto [&_iframe]:aspect-video [&_iframe]:rounded-xl [&_iframe]:my-6 [&_iframe]:border-0"
                dangerouslySetInnerHTML={{ __html: localizeHtml(safeSanitize(data.description || '')) }}
            />
        </div>
    );
}

// ─── product-variants ────────────────────────────────────────────────────────

export function ProductVariantsBlock(props: Partial<BlockProps> & { product?: any }) {
    const { block, currentStyles = {}, isAdmin, product } = props;
    const { t } = useTranslation();
    const productState = useProductState();
    const data = productState?.product || product || (isAdmin ? DEMO_PRODUCT : null);

    const [localSelectedAttributes, setLocalSelectedAttributes] = useState<Record<string, string>>({});
    const [attributeCatalog, setAttributeCatalog] = useState<any[]>([]);

    const selectedAttributes = productState ? productState.selectedAttributes : localSelectedAttributes;

    useEffect(() => {
        if (!productState && data?.product_type === 'variable') {
            const defVariant = data.variants?.find((v: any) => v.is_default) || data.variants?.[0];
            if (defVariant) setLocalSelectedAttributes(defVariant.attributes || {});
        }
    }, [productState, data]);

    useEffect(() => {
        fetchAPI('/attributes').then((res) => { if (Array.isArray(res)) setAttributeCatalog(res); }).catch(() => {});
    }, []);

    if (!data) return null;
    if (usesConfigurator(data)) {
        return <ProductConfiguratorBlock {...props} />;
    }
    if (data.product_type !== 'variable') return null;

    const attributesMap: Record<string, Set<string>> = {};
    (data.variants || []).forEach((v: any) => {
        Object.entries(v.attributes).forEach(([key, val]: [string, any]) => {
            if (!attributesMap[key]) attributesMap[key] = new Set();
            attributesMap[key].add(val);
        });
    });

    if (Object.keys(attributesMap).length === 0) return null;

    const s = block?.settings || {};
    // Sizing fields (radius/border-width/gap/spacing/padding/font-size) are responsive —
    // currentStyles (styles[viewport] merge) takes priority, falling back to the old flat
    // settings key so pages configured before this existed keep their look.
    const varBorderRadius = currentStyles.varBorderRadius ?? s.varBorderRadius ?? 8;
    const varBorderWidth = currentStyles.varBorderWidth ?? s.varBorderWidth ?? 1;
    const varBgColor = s.varBgColor || '#ffffff';
    const varTextColor = s.varTextColor || '#475569';
    const varBorderColor = s.varBorderColor || '#e2e8f0';
    const varHoverBgColor = s.varHoverBgColor || '#f8fafc';
    const varHoverTextColor = s.varHoverTextColor || '#0f172a';
    const varHoverBorderColor = s.varHoverBorderColor || '#94a3b8';
    const varSelectedBgColor = s.varSelectedBgColor || '#0f172a';
    const varSelectedTextColor = s.varSelectedTextColor || '#ffffff';
    const varSelectedBorderColor = s.varSelectedBorderColor || '#0f172a';
    const varSwatchSizePx = currentStyles.varSwatchSize ?? s.varSwatchSize ?? 40;
    const varSwatchShape: 'circle' | 'rounded' | 'square' = s.varSwatchShape ?? 'circle';
    const SWATCH_SHAPE_CLASS: Record<string, string> = { circle: 'rounded-full', rounded: 'rounded-lg', square: 'rounded-none' };

    const varAlign = s.varAlign || 'left';
    const ALIGN_CLASS: Record<string, string> = {
        left: 'justify-start',
        center: 'justify-center',
        right: 'justify-end',
        between: 'justify-between',
    };
    const varLabelAlign = s.varLabelAlign || 'left';
    const LABEL_ALIGN_CLASS: Record<string, string> = {
        left: 'justify-start text-left',
        center: 'justify-center text-center',
        right: 'justify-end text-right',
    };

    const varFullWidthButtons = !!s.varFullWidthButtons;
    const varItemGap = currentStyles.varItemGap ?? s.varItemGap ?? 8;
    const varGroupSpacing = currentStyles.varGroupSpacing ?? s.varGroupSpacing ?? 24;
    const varShowDivider = s.varShowDivider ?? true;
    const varDividerColor = s.varDividerColor || '#f1f5f9';

    const varLabelFontSize = currentStyles.varLabelFontSize ?? s.varLabelFontSize ?? 14;
    const varLabelFontWeight = s.varLabelFontWeight || 'bold';
    const varLabelColor = s.varLabelColor || '#0f172a';
    const varLabelUppercase = s.varLabelUppercase ?? true;

    const varBtnPaddingY = currentStyles.varBtnPaddingY ?? s.varBtnPaddingY ?? 8;
    const varBtnPaddingX = currentStyles.varBtnPaddingX ?? s.varBtnPaddingX ?? 16;

    const cls = `var-btn-${block?.id ?? 'default'}`;

    const getAttrDisplayInfo = (attrName: string) => {
        return attributeCatalog.find(a => a.name.toLowerCase() === attrName.toLowerCase()) || null;
    };

    return (
        <div className={isAdmin ? 'relative' : ''}>
            {isAdmin && <DemoBadge />}
            <style>{`
                .${cls} {
                    background-color: ${varBgColor};
                    color: ${varTextColor};
                    border-color: ${varBorderColor};
                    border-width: ${varBorderWidth}px;
                    border-style: solid;
                    border-radius: ${varBorderRadius}px;
                    padding: ${varBtnPaddingY}px ${varBtnPaddingX}px;
                    transition: all 150ms;
                }
                .${cls}:hover {
                    background-color: ${varHoverBgColor};
                    color: ${varHoverTextColor};
                    border-color: ${varHoverBorderColor};
                }
                .${cls}.is-selected {
                    background-color: ${varSelectedBgColor};
                    color: ${varSelectedTextColor};
                    border-color: ${varSelectedBorderColor};
                }
            `}</style>
            <div
                className="py-6"
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: `${varGroupSpacing}px`,
                }}
            >
                {Object.entries(attributesMap).map(([attrName, values], idx) => {
                    const catalogAttr = getAttrDisplayInfo(attrName);
                    const displayType = catalogAttr?.display_type || 'text';
                    const isLast = idx === Object.keys(attributesMap).length - 1;

                    return (
                        <div
                            key={attrName}
                            className="space-y-2.5"
                            style={{
                                borderBottom: (!isLast && varShowDivider) ? `1px solid ${varDividerColor}` : undefined,
                                paddingBottom: (!isLast && varShowDivider) ? `${varGroupSpacing}px` : undefined,
                            }}
                        >
                            <div className={cn('flex items-center', LABEL_ALIGN_CLASS[varLabelAlign] || 'justify-start')}>
                                <p
                                    style={{
                                        fontSize: `${varLabelFontSize}px`,
                                        fontWeight: varLabelFontWeight,
                                        color: varLabelColor,
                                        textTransform: varLabelUppercase ? 'uppercase' : 'none',
                                        letterSpacing: varLabelUppercase ? '0.05em' : 'normal',
                                    }}
                                >
                                    {attrName}: <span className="font-normal opacity-80">{selectedAttributes[attrName] || '—'}</span>
                                </p>
                            </div>
                            <div
                                className={cn('flex flex-wrap items-center', ALIGN_CLASS[varAlign] || 'justify-start')}
                                style={{ gap: `${varItemGap}px` }}
                            >
                                {Array.from(values).map((val) => {
                                    const isSelected = selectedAttributes[attrName] === val;
                                    const catalogVal = catalogAttr?.values?.find((v: any) => v.value.toLowerCase() === val.toLowerCase());
                                    const colorHex = catalogVal?.color_hex;
                                    const imgSrc = catalogVal?.image_path ? getAbsoluteMediaUrl(catalogVal.image_path) : null;

                                    const onSelect = () => {
                                        if (productState) {
                                            productState.handleAttributeSelect(attrName, val);
                                        } else {
                                            setLocalSelectedAttributes((prev) => ({ ...prev, [attrName]: val }));
                                        }
                                    };

                                    if (displayType === 'color' || displayType === 'image_text') {
                                        return (
                                            <button
                                                key={val}
                                                type="button"
                                                title={val}
                                                onClick={onSelect}
                                                className="flex flex-col items-center gap-1 p-1 cursor-pointer"
                                            >
                                                <span
                                                    className={cn(
                                                        'block overflow-hidden border-2 transition-all',
                                                        SWATCH_SHAPE_CLASS[varSwatchShape],
                                                        !colorHex && !imgSrc && 'bg-gray-100',
                                                        isSelected ? 'ring-2' : 'border-slate-200'
                                                    )}
                                                    style={{
                                                        width: varSwatchSizePx,
                                                        height: varSwatchSizePx,
                                                        backgroundColor: colorHex || undefined,
                                                        borderColor: isSelected ? varSelectedBorderColor : varBorderColor,
                                                        outlineColor: isSelected ? varSelectedBgColor : undefined,
                                                    }}
                                                >
                                                    {imgSrc && (
                                                        <img src={imgSrc} alt={val} className="w-full h-full object-cover" />
                                                    )}
                                                </span>
                                            </button>
                                        );
                                    }

                                    return (
                                        <button
                                            key={val}
                                            onClick={onSelect}
                                            className={cn(
                                                cls,
                                                'text-sm font-medium cursor-pointer',
                                                varFullWidthButtons && 'flex-1 min-w-[120px] text-center',
                                                isSelected && 'is-selected'
                                            )}
                                        >
                                            {val}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ─── product-configurator (a 'configurable' product, or a service with options) ──

function ProductConfiguratorBlock(props: Partial<BlockProps> & { product?: any }) {
    const { block, currentStyles = {}, isAdmin, product } = props;
    const { t, currency, locale } = useTranslation();
    const productState = useProductState();
    const data = productState?.product || product || (isAdmin ? DEMO_PRODUCT : null);

    const [configurator, setConfigurator] = useState<{
        product_type: string;
        product_attributes: any[];
        conditions: any[];
    } | null>(null);
    const [loading, setLoading] = useState(true);
    const [selections, setSelections] = useState<Record<number, number[]>>({});
    // One quantity for the whole page: a product-quantity block elsewhere on the page and this
    // block's own field (shown only when there's no such block) edit the same number.
    const [localQuantity, setLocalQuantity] = useState(1);
    const minLoad = serviceMinQuantity(data);
    const quantity = Math.max(minLoad, productState ? productState.quantity : localQuantity);
    const setQuantity = productState ? productState.setQuantity : setLocalQuantity;
    const [adding, setAdding] = useState(false);

    useEffect(() => {
        if (!data?.id) return;
        setLoading(true);
        fetchAPI(`/products/${data.id}/configurator`)
            .then((res) => { if (res) setConfigurator(res); })
            .catch(() => {})
            .finally(() => setLoading(false));
        // `currency` must stay a dependency: get_product_configurator() resolves each
        // attribute value's resolved_price_modifier for the currently requested currency
        // (via X-Currency, read from the stag_currency cookie) — a single scalar computed
        // fresh per request, unlike currency_price_modifiers which already carries every
        // currency at once and needed no refetch. Without this, a currency switch that
        // doesn't reload the page (or any stale in-memory configurator state) leaves
        // computedPrice's attribute deltas frozen at whatever currency was active on mount.
    }, [data?.id, currency]);

    const productAttributes = configurator?.product_attributes || [];
    const conditions = configurator?.conditions || [];
    const evaluation = evaluateConfigurator(selections, productAttributes as any, conditions as any, currency);
    const complete = isConfiguratorComplete(selections, productAttributes as any, evaluation);

    const basePrice = data?.sales_price ?? data?.price ?? 0;
    const computedPrice = Math.round((basePrice + evaluation.priceDelta) * 100) / 100;
    // A service with options keeps its own pricing rules: a flat-rate one is a single charge (no
    // count to pick — create_order clamps it to 1 anyway), a per-kg one asks for the estimated
    // weight and shows the price per kg.
    const servicePricing = data?.product_type === 'service' ? (data.service_config?.pricing_type || 'unit') : null;
    const lineQuantity = servicePricing === 'flat' ? 1 : quantity;
    // Per m²: the line is one measured piece size (ServiceMeasureFields), priced rate × area.
    const measure = servicePricing === 'area' ? productState?.serviceMeasure ?? null : null;

    const getValModifier = (v: any): number => {
        const activeCurr = (currency || 'PLN').toUpperCase();
        if (v.currency_price_modifiers && v.currency_price_modifiers[activeCurr] !== undefined && v.currency_price_modifiers[activeCurr] !== null) {
            return Number(v.currency_price_modifiers[activeCurr]);
        }
        // Backend-converted fallback for values with no explicit override — see evaluateConfigurator.
        if (v.resolved_price_modifier !== undefined && v.resolved_price_modifier !== null) {
            return v.resolved_price_modifier;
        }
        return v.price_modifier || 0;
    };

    const handleAddToCart = (): boolean => {
        if (!data || !complete) {
            toast.error(t('product.configurator_incomplete', 'Uzupełnij wszystkie wymagane pola konfiguratora.'));
            return false;
        }
        if (servicePricing === 'area' && !measure) {
            toast.error(t('product.measure_required', 'Podaj wymiary, żeby dodać usługę do koszyka.'));
            return false;
        }
        setAdding(true);
        try {
            const selectionEntries = productAttributes
                .filter((pa: any) => evaluation.byAttributeId[pa.attribute_id]?.visible && (selections[pa.attribute_id]?.length ?? 0) > 0)
                .map((pa: any) => {
                    const state = evaluation.byAttributeId[pa.attribute_id];
                    const rawValueIds = selections[pa.attribute_id] || [];
                    const activeCount = state?.maxSelectCount && state.maxSelectCount > 1
                        ? state.maxSelectCount
                        : Math.max(1, state?.repeatCount || 1);
                    const valueIds = rawValueIds.slice(0, activeCount);
                    const values = valueIds.map((vid: number) => pa.attribute.values.find((v: any) => v.id === vid)).filter(Boolean);
                    return {
                        attribute_id: pa.attribute_id,
                        attribute_name: pa.attribute.name,
                        value_ids: valueIds,
                        value_labels: values.map((v: any) => v.value),
                        price_delta: values.reduce((sum: number, v: any) => sum + getValModifier(v), 0),
                    };
                });

            const mainImage = data.images?.find((img: any) => img.is_main) || data.images?.[0];
            const imageUrl = mainImage ? getAbsoluteMediaUrl(mainImage.path) : null;

            addConfigurableToCart(
                { id: data.id, name: measure ? `${data.name} (${measure.width_cm} × ${measure.length_cm} cm)` : data.name, slug: data.slug },
                selectionEntries,
                measure ? Math.round(computedPrice * serviceBillableArea(data, measure) * 100) / 100 : computedPrice,
                lineQuantity,
                imageUrl,
                {
                    product_type: data.product_type,
                    requires_shipping: data.requires_shipping,
                    ...(minLoad > 1 ? { min_quantity: minLoad } : {}),
                    ...(measure ? { service_measure: measure } : {}),
                },
            );
            toast.success(t('product.add_to_cart_success'));
            return true;
        } finally {
            setAdding(false);
        }
    };

    // Auto-select initial default values for every visible attribute & repeated slot
    useEffect(() => {
        if (!configurator || productAttributes.length === 0) return;

        setSelections((prev) => {
            let changed = false;
            const next = { ...prev };

            for (const pa of productAttributes) {
                const state = evaluation.byAttributeId[pa.attribute_id];
                if (!state?.visible) continue;
                const values = (pa.attribute?.values || []).filter(
                    (v: any) => !state.allowedValueIds || state.allowedValueIds.has(v.id)
                );
                if (values.length === 0) continue;

                const repeatCount = Math.max(1, state.repeatCount || 1);
                const currentVids = next[pa.attribute_id] || [];

                for (let slotIdx = 0; slotIdx < repeatCount; slotIdx++) {
                    const currentVid = currentVids[slotIdx];
                    const isValid = currentVid && values.some((v: any) => v.id === currentVid);
                    if (!isValid) {
                        if (!next[pa.attribute_id]) next[pa.attribute_id] = [];
                        next[pa.attribute_id][slotIdx] = values[0].id;
                        changed = true;
                    }
                }
            }

            return changed ? next : prev;
        });
    }, [configurator, productAttributes, evaluation]);

    useEffect(() => {
        if (productState?.setConfiguratorPrice) {
            productState.setConfiguratorPrice(computedPrice);
        }
    }, [computedPrice, productState?.setConfiguratorPrice]);

    useEffect(() => {
        if (productState?.setConfigurableAddToCart) {
            productState.setConfigurableAddToCart(() => handleAddToCart);
        }
    }, [complete, selections, productAttributes, computedPrice, lineQuantity, measure, productState?.setConfigurableAddToCart]);

    const s = block?.settings || {};
    // Sizing fields (radius/border-width/gap/spacing/padding/font-size) are responsive —
    // currentStyles (styles[viewport] merge) takes priority, falling back to the old flat
    // settings key so pages configured before this existed keep their look.
    const varBorderRadius = currentStyles.varBorderRadius ?? s.varBorderRadius ?? 8;
    const varBorderWidth = currentStyles.varBorderWidth ?? s.varBorderWidth ?? 1;
    const varBgColor = s.varBgColor || '#ffffff';
    const varTextColor = s.varTextColor || '#475569';
    const varBorderColor = s.varBorderColor || '#e2e8f0';
    const varHoverBgColor = s.varHoverBgColor || '#f8fafc';
    const varHoverTextColor = s.varHoverTextColor || '#0f172a';
    const varHoverBorderColor = s.varHoverBorderColor || '#94a3b8';
    const varSelectedBgColor = s.varSelectedBgColor || '#0f172a';
    const varSelectedTextColor = s.varSelectedTextColor || '#ffffff';
    const varSelectedBorderColor = s.varSelectedBorderColor || '#0f172a';
    const varSwatchSizePx = currentStyles.varSwatchSize ?? s.varSwatchSize ?? 40;
    const varSwatchShape: 'circle' | 'rounded' | 'square' = s.varSwatchShape ?? 'circle';
    const SWATCH_SHAPE_CLASS: Record<string, string> = { circle: 'rounded-full', rounded: 'rounded-lg', square: 'rounded-none' };

    const varAlign = s.varAlign || 'left';
    const ALIGN_CLASS: Record<string, string> = {
        left: 'justify-start',
        center: 'justify-center',
        right: 'justify-end',
        between: 'justify-between',
    };
    const varLabelAlign = s.varLabelAlign || 'left';
    const LABEL_ALIGN_CLASS: Record<string, string> = {
        left: 'justify-start text-left',
        center: 'justify-center text-center',
        right: 'justify-end text-right',
    };

    const varFullWidthButtons = !!s.varFullWidthButtons;
    const varItemGap = currentStyles.varItemGap ?? s.varItemGap ?? 8;
    const varGroupSpacing = currentStyles.varGroupSpacing ?? s.varGroupSpacing ?? 24;
    const varShowDivider = s.varShowDivider ?? true;
    const varDividerColor = s.varDividerColor || '#f1f5f9';

    const varLabelFontSize = currentStyles.varLabelFontSize ?? s.varLabelFontSize ?? 14;
    const varLabelFontWeight = s.varLabelFontWeight || 'bold';
    const varLabelColor = s.varLabelColor || '#0f172a';
    const varLabelUppercase = s.varLabelUppercase ?? true;

    const varShowPriceModifier = s.varShowPriceModifier ?? true;
    const varPriceModifierColor = s.varPriceModifierColor || '#64748b';

    const varBtnPaddingY = currentStyles.varBtnPaddingY ?? s.varBtnPaddingY ?? 8;
    const varBtnPaddingX = currentStyles.varBtnPaddingX ?? s.varBtnPaddingX ?? 16;

    const cls = `cfg-btn-${block?.id ?? 'default'}`;

    // Whatever the page template already shows in its own blocks (price, quantity, the
    // "Dodaj do koszyka" / "Kup teraz" buttons, which call handleAddToCart through the product
    // context) is left out here — the product page used to show two price lines and two
    // add-to-cart buttons. A page without those blocks still gets the full footer.
    const pageControls = productState?.pageControls || {};
    const ownPriceClass = ownControlClass(pageControls, 'price');
    const ownQuantityClass = servicePricing !== 'flat' ? ownControlClass(pageControls, 'quantity') : undefined;
    const ownButtonClass = ownControlClass(pageControls, 'add-to-cart');
    const showOwnPrice = ownPriceClass !== undefined;
    const showOwnQuantity = ownQuantityClass !== undefined;
    const showOwnButton = ownButtonClass !== undefined;
    // The footer (with its top border) goes where none of the three copies is shown.
    const footerClass = ALL_BREAKPOINTS
        .filter((bp) => [ownPriceClass, ownQuantityClass, ownButtonClass].every((c) => c === undefined || c.includes(`block-hide-${bp}`)))
        .map((bp) => `block-hide-${bp}`).join(' ');

    if (!data) return null;

    if (isAdmin) {
        return (
            <div className="relative border-y border-slate-100 py-6">
                <DemoBadge />
                <p className="text-sm text-slate-500 italic">
                    Konfigurator produktu (podgląd dostępny na sklepie — wymaga zapisanego produktu typu &quot;Konfigurowalny&quot;).
                </p>
            </div>
        );
    }

    if (loading) {
        return <div className="py-6 text-sm text-slate-400">{t('product.configurator_loading', 'Ładowanie konfiguratora…')}</div>;
    }

    const setAttributeSelection = (attributeId: number, valueId: number, maxSelect?: number | null, slotIndex: number = 0) => {
        setSelections((prev) => {
            const current = prev[attributeId] || [];
            if (maxSelect && maxSelect > 1) {
                if (current.includes(valueId)) {
                    return { ...prev, [attributeId]: current.filter((v) => v !== valueId) };
                }
                if (current.length >= maxSelect) {
                    toast.error(t('product.configurator_max_select', 'Możesz wybrać maksymalnie {n} opcji.').replace('{n}', String(maxSelect)));
                    return prev;
                }
                return { ...prev, [attributeId]: [...current, valueId] };
            }
            const updated = [...current];
            updated[slotIndex] = valueId;
            return { ...prev, [attributeId]: updated };
        });
    };

    return (
        <div className={isAdmin ? 'relative' : ''}>
            <style>{`
                .${cls} {
                    background-color: ${varBgColor};
                    color: ${varTextColor};
                    border-color: ${varBorderColor};
                    border-width: ${varBorderWidth}px;
                    border-style: solid;
                    border-radius: ${varBorderRadius}px;
                    padding: ${varBtnPaddingY}px ${varBtnPaddingX}px;
                    transition: all 150ms;
                }
                .${cls}:hover {
                    background-color: ${varHoverBgColor};
                    color: ${varHoverTextColor};
                    border-color: ${varHoverBorderColor};
                }
                .${cls}.is-selected {
                    background-color: ${varSelectedBgColor};
                    color: ${varSelectedTextColor};
                    border-color: ${varSelectedBorderColor};
                }
            `}</style>
            <div
                className="py-6"
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: `${varGroupSpacing}px`,
                }}
            >
                {productAttributes.map((pa: any, paIdx: number) => {
                    const state = evaluation.byAttributeId[pa.attribute_id];
                    if (!state?.visible) return null;
                    const attribute = pa.attribute;
                    const displayType = attribute.display_type || 'text';
                    const selectedValueIds = selections[pa.attribute_id] || [];
                    const values = (attribute.values || []).filter((v: any) => !state.allowedValueIds || state.allowedValueIds.has(v.id));
                    const maxSelect = state.maxSelectCount;
                    const repeatCount = Math.max(1, state.repeatCount || 1);
                    const isLast = paIdx === productAttributes.length - 1;

                    return (
                        <div
                            key={pa.attribute_id}
                            className="space-y-4"
                            style={{
                                borderBottom: (!isLast && varShowDivider) ? `1px solid ${varDividerColor}` : undefined,
                                paddingBottom: (!isLast && varShowDivider) ? `${varGroupSpacing}px` : undefined,
                            }}
                        >
                            {Array.from({ length: repeatCount }).map((_, slotIdx) => {
                                const selectedValueId = selectedValueIds[slotIdx];
                                const slotLabel = repeatCount > 1 ? `${attribute.name} #${slotIdx + 1}` : attribute.name;

                                return (
                                    <div key={slotIdx} className="space-y-2.5">
                                        <div className={cn('flex items-center justify-between', LABEL_ALIGN_CLASS[varLabelAlign] || 'justify-start')}>
                                            <p
                                                style={{
                                                    fontSize: `${varLabelFontSize}px`,
                                                    fontWeight: varLabelFontWeight,
                                                    color: varLabelColor,
                                                    textTransform: varLabelUppercase ? 'uppercase' : 'none',
                                                    letterSpacing: varLabelUppercase ? '0.05em' : 'normal',
                                                }}
                                            >
                                                {slotLabel}{pa.required && <span className="text-red-500"> *</span>}
                                            </p>
                                            {maxSelect && maxSelect > 1 && (
                                                <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-md">
                                                    Wybrano {selectedValueIds.length} z {maxSelect}
                                                </span>
                                            )}
                                        </div>

                                        {displayType === 'dropdown' ? (
                                            <Combobox
                                                className="max-w-xs"
                                                placeholder="Wybierz..."
                                                value={selectedValueId ?? ''}
                                                onChange={(val) => setAttributeSelection(pa.attribute_id, Number(val), maxSelect, slotIdx)}
                                                options={values.map((v: any) => {
                                                    const mod = getValModifier(v);
                                                    return {
                                                        value: v.id,
                                                        label: (mod > 0 && varShowPriceModifier) ? `${v.value} (+${formatPrice(mod, currency, locale)})` : v.value,
                                                        colorHex: v.color_hex,
                                                        imagePath: v.image_path ? getMediaUrl(v.image_path) : null,
                                                    };
                                                })}
                                            />
                                        ) : displayType === 'color' || displayType === 'image_text' ? (
                                            <div
                                                className={cn('flex flex-wrap items-center', ALIGN_CLASS[varAlign] || 'justify-start')}
                                                style={{ gap: `${varItemGap}px` }}
                                            >
                                                {values.map((v: any) => {
                                                    const isSelected = maxSelect && maxSelect > 1 ? selectedValueIds.includes(v.id) : selectedValueId === v.id;
                                                    const imgSrc = displayType === 'image_text' && v.image_path ? getMediaUrl(v.image_path) : null;
                                                    const mod = getValModifier(v);
                                                    const titleLabel = mod > 0 ? `${v.value} (+${formatPrice(mod, currency, locale)})` : v.value;
                                                    return (
                                                        <button
                                                            key={v.id}
                                                            type="button"
                                                            title={titleLabel}
                                                            onClick={() => setAttributeSelection(pa.attribute_id, v.id, maxSelect, slotIdx)}
                                                            className="flex flex-col items-center gap-1 p-1 cursor-pointer"
                                                        >
                                                            <span
                                                                className={cn(
                                                                    'block overflow-hidden border-2 transition-all',
                                                                    SWATCH_SHAPE_CLASS[varSwatchShape],
                                                                    !v.color_hex && !imgSrc && 'bg-gray-100',
                                                                    isSelected ? 'ring-2' : 'border-slate-200',
                                                                )}
                                                                style={{
                                                                    width: varSwatchSizePx,
                                                                    height: varSwatchSizePx,
                                                                    backgroundColor: v.color_hex || undefined,
                                                                    borderColor: isSelected ? varSelectedBorderColor : varBorderColor,
                                                                    outlineColor: isSelected ? varSelectedBgColor : undefined,
                                                                }}
                                                            >
                                                                {imgSrc && <img src={imgSrc} alt={v.value} className="w-full h-full object-cover" />}
                                                            </span>
                                                            <span className="text-[11px] font-medium text-slate-600">
                                                                {v.value}
                                                                {mod > 0 && varShowPriceModifier && (
                                                                    <span style={{ color: varPriceModifierColor }}> (+{formatPrice(mod, currency, locale)})</span>
                                                                )}
                                                            </span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <div
                                                className={cn('flex flex-wrap items-center', ALIGN_CLASS[varAlign] || 'justify-start')}
                                                style={{ gap: `${varItemGap}px` }}
                                            >
                                                {values.map((v: any) => {
                                                    const isSelected = maxSelect && maxSelect > 1 ? selectedValueIds.includes(v.id) : selectedValueId === v.id;
                                                    const mod = getValModifier(v);
                                                    return (
                                                        <button
                                                            key={v.id}
                                                            type="button"
                                                            onClick={() => setAttributeSelection(pa.attribute_id, v.id, maxSelect, slotIdx)}
                                                            className={cn(
                                                                cls,
                                                                'text-sm font-medium cursor-pointer',
                                                                varFullWidthButtons && 'flex-1 min-w-[120px] text-center',
                                                                isSelected && 'is-selected'
                                                            )}
                                                        >
                                                            <span>{v.value}</span>
                                                            {mod > 0 && varShowPriceModifier && (
                                                                <span
                                                                    className="ml-1 text-[11px] opacity-80"
                                                                    style={{ color: isSelected ? 'inherit' : varPriceModifierColor }}
                                                                >
                                                                    (+{formatPrice(mod, currency, locale)})
                                                                </span>
                                                            )}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}

                {(showOwnPrice || showOwnQuantity || showOwnButton) && (
                <div className={cn("flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-slate-100", footerClass)}>
                    {showOwnPrice && (
                    <div className={ownPriceClass}>
                        <p className="text-xs text-slate-400 uppercase font-bold tracking-wide">{t('product.price_label', 'Cena')}</p>
                        <p className="text-2xl font-black text-slate-900">
                            {formatPrice(computedPrice, currency, locale)}
                            {servicePricing === 'weight' && <span className="ml-1 text-sm font-semibold text-slate-500">{t('product.per_kg', '/ kg')}</span>}
                            {servicePricing === 'area' && <span className="ml-1 text-sm font-semibold text-slate-500">{t('product.per_m2', '/ m²')}</span>}
                        </p>
                    </div>
                    )}
                    {showOwnButton && servicePricing === 'area' && (
                        <div className={cn("basis-full", ownButtonClass)}><ServiceMeasureFields product={data} /></div>
                    )}
                    <div className="flex flex-wrap items-center gap-3">
                        {showOwnQuantity && (
                            <label className={cn("flex items-center gap-2", ownQuantityClass)}>
                                <span className="text-xs text-slate-500 max-w-[7rem] leading-tight">
                                    {servicePricing === 'weight' ? t('product.quantity_weight', 'Szacowana waga (kg)') : t('product.quantity', 'Ilość')}
                                </span>
                                <input
                                    type="number"
                                    min={minLoad}
                                    value={quantity}
                                    onChange={(e) => setQuantity(Math.max(minLoad, parseInt(e.target.value, 10) || minLoad))}
                                    aria-label={servicePricing === 'weight' ? t('product.quantity_weight', 'Szacowana waga (kg)') : t('product.quantity', 'Ilość')}
                                    className="w-16 h-10 text-center border border-slate-200 rounded-lg text-sm font-bold"
                                />
                            </label>
                        )}
                        {showOwnButton && (
                        <Button size="lg" onClick={() => handleAddToCart()} disabled={!complete || adding || (servicePricing === 'area' && !measure)} className={cn("gap-2", ownButtonClass)}>
                            <ShoppingCart className="h-5 w-5" />
                            {t('product.add_to_cart')}
                        </Button>
                        )}
                    </div>
                    {showOwnQuantity && minLoad > 1 && (
                        <p className={cn("basis-full text-xs text-slate-500", ownQuantityClass)}>
                            {(servicePricing === 'weight'
                                ? t('product.min_load_kg', 'Minimalny wsad: {n} kg — mniejszą ilość rozliczamy jak {n} kg.')
                                : t('product.min_load_units', 'Minimalne zamówienie: {n} szt.')).replaceAll('{n}', String(minLoad))}
                        </p>
                    )}
                </div>
                )}
            </div>
        </div>
    );
}

// ─── product-add-to-cart ─────────────────────────────────────────────────────

export function ProductAddToCartBlock({ block, currentStyles, isAdmin, product, viewport, ...blockProps }: BlockProps) {
    const { t, tPath, currency } = useTranslation();
    const router = useRouter();
    const { data: session } = useSession();
    const [isAdded, setIsAdded] = useState(false);
    const siteConfig = useSiteConfig();
    const [showLoginGate, setShowLoginGate] = useState(false);
    const [showNegotiateModal, setShowNegotiateModal] = useState(false);

    // These must stay above the `isAdmin`/`!activeProduct` early returns further down — a hook
    // placed after a conditional return fires on some renders and not others (e.g. never in the
    // admin/builder preview branch), which React rejects with error #310 the moment the branch
    // taken differs from the previous render. The cart-check effect already no-ops in admin mode
    // via its own internal guard, so moving it up here doesn't change its behavior.
    const productState = useProductState();
    const activeProduct = productState?.product || product;
    const activeVariant = productState?.selectedVariant;
    const [isInCart, setIsInCart] = useState(false);
    useEffect(() => {
        if (isAdmin || !activeProduct || activeProduct.product_type === 'variable' || !activeProduct.is_one_off) return;
        const checkCart = () => {
            const cart = JSON.parse(localStorage.getItem('stag-cart') || '[]');
            const cartItemId = activeVariant ? `${activeProduct.id}-${activeVariant.id}` : `${activeProduct.id}`;
            setIsInCart(cart.some((item: any) => item.cartItemId === cartItemId));
        };
        checkCart();
        window.addEventListener('cart-updated', checkCart);
        return () => window.removeEventListener('cart-updated', checkCart);
    }, [activeProduct?.id, activeProduct?.product_type, activeProduct?.is_one_off, activeVariant, isAdmin]);
    const [showAddToCartModal, setShowAddToCartModal] = useState(false);
    const mainButtonRef = useRef<HTMLDivElement>(null);

    const settings = block.settings || {};
    const buttonText: string = settings.buttonText ?? t('product.add_to_cart');
    const showAddToCart: boolean = settings.showAddToCart !== false;
    const showBuyNow: boolean = settings.showBuyNow === true;
    const buyNowText: string = settings.buyNowText ?? t('product.buy_now', 'Kup teraz');
    // "Na zamówienie" — shown instead of buttonText/buyNowText when the product is out of
    // stock but its stock_zero_action is "allow" (see isBackorder below).
    const backorderButtonText: string = settings.backorderButtonText?.trim() || t('product.backorder', 'Na zamówienie');
    const showNegotiate: boolean = settings.showNegotiate === true
        && (isAdmin || isNegotiationAllowedForProduct(siteConfig, product));
    const negotiateText: string = settings.negotiateText || siteConfig?.price_negotiation_button_label || t('negotiation.negotiate_price', 'Negocjuj cenę');
    usePageControl('add-to-cart', !isAdmin && (showAddToCart || showBuyNow));

    // Individual per-button icons
    const btnIconType: string = settings.btnIconType ?? settings.iconType ?? 'cart';
    const btnIconPosition: 'left' | 'right' = settings.btnIconPosition ?? settings.iconPosition ?? 'left';
    const btnIconEl = btnIconType !== 'none' ? <ProductActionIcon iconType={btnIconType} /> : null;

    const buyNowIconType: string = settings.buyNowIconType ?? 'none';
    const buyNowIconPosition: 'left' | 'right' = settings.buyNowIconPosition ?? 'left';
    const buyNowIconEl = buyNowIconType !== 'none' ? <ProductActionIcon iconType={buyNowIconType} /> : null;

    const negotiateIconType: string = settings.negotiateIconType ?? 'none';
    const negotiateIconPosition: 'left' | 'right' = settings.negotiateIconPosition ?? 'left';
    const negotiateIconEl = negotiateIconType !== 'none' ? <ProductActionIcon iconType={negotiateIconType} /> : null;

    const fullWidth: boolean = settings.fullWidth === true;
    const justifyContent: string = currentStyles.justifyContent || 'flex-start';

    // Count how many buttons are actually visible
    const activeButtonsCount = [
        showAddToCart && 'addToCart',
        showBuyNow && 'buyNow',
        showNegotiate && 'negotiate',
    ].filter(Boolean).length;
    const isSingleButton = activeButtonsCount <= 1;

    // Layout of the buttons relative to each other — only relevant when multiple buttons exist.
    // When only 1 button is active, column layout or wrapping is unnecessary and causes mobile overflow.
    const buttonsLayout: 'row' | 'column' = isSingleButton
        ? 'row'
        : (currentStyles.buttonsLayout === 'column' ? 'column' : 'row');

    const columnAlignItems = justifyContent === 'center'
        ? 'center'
        : justifyContent === 'flex-end'
            ? 'flex-end'
            : 'flex-start';

    const cls = `atc-btn-${block.id}`;
    const buyNowCls = `atc-buynow-btn-${block.id}`;
    const negotiateCls = `atc-negotiate-btn-${block.id}`;
    const safeBlockId = String(block.id).replace(/[^a-zA-Z0-9_-]/g, '');

    // Button sizing (padding/font-size/radius/border-width/width) is responsive — the editor
    // writes it to styles[viewport], which currentStyles already merges in here — layered over
    // settings so it still falls back correctly for buttons/colors that stay settings-only.
    const buttonStyleSettings = { ...settings, ...currentStyles };
    const buttonStyle = buildActionButtonStyle(buttonStyleSettings, 'btn', ACTION_BUTTON_DEFAULTS.btn);
    const buyNowButtonStyle = buildActionButtonStyle(buttonStyleSettings, 'buyNow', ACTION_BUTTON_DEFAULTS.buyNow);
    const negotiateButtonStyle = buildActionButtonStyle(buttonStyleSettings, 'negotiate', ACTION_BUTTON_DEFAULTS.negotiate);

    const wrapperStyle: React.CSSProperties = {
        display: 'flex',
        justifyContent,
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
    };

    const buttonsRowStyle: React.CSSProperties = {
        display: 'flex',
        flexDirection: buttonsLayout,
        alignItems: buttonsLayout === 'column' ? columnAlignItems : 'center',
        justifyContent: isSingleButton ? justifyContent : undefined,
        gap: isSingleButton ? undefined : '0.5rem',
        flexWrap: isSingleButton ? 'nowrap' : 'wrap',
        width: fullWidth ? '100%' : undefined,
        maxWidth: '100%',
        boxSizing: 'border-box',
    };

    // The "Animacje i Efekty" panel's hover effect (scale/lift/tilt/glow) is applied to
    // each button below, not to the wrapper — the wrapper spans the whole button row
    // (gap included), so hovering that empty space used to grow/tilt the whole block
    // instead of just the button under the cursor (see HOVER_SCOPED_TO_INNER_ELEMENT).
    const hoverEffectClass = getHoverClass(currentStyles.hoverEffect);
    const hoverEffectVars: React.CSSProperties = {
        '--hover-scale': currentStyles.hoverScaleValue || undefined,
        '--hover-lift': currentStyles.hoverLiftDistance ? `${currentStyles.hoverLiftDistance}px` : undefined,
        '--hover-tilt': currentStyles.hoverTiltAngle ? `${currentStyles.hoverTiltAngle}deg` : undefined,
        '--hover-glow-color': currentStyles.hoverGlowColor || undefined,
        '--hover-glow-blur': currentStyles.hoverGlowBlur ? `${currentStyles.hoverGlowBlur}px` : undefined,
        '--hover-duration': currentStyles.hoverTransitionDuration || undefined,
    } as any;

    const hoverStyle = [
        buildActionButtonHoverCSS(settings, 'btn', cls, ACTION_BUTTON_DEFAULTS.btn),
        buildActionButtonHoverCSS(settings, 'buyNow', buyNowCls, ACTION_BUTTON_DEFAULTS.buyNow),
        buildActionButtonHoverCSS(settings, 'negotiate', negotiateCls, ACTION_BUTTON_DEFAULTS.negotiate),
    ].join('\n');

    // Sticky mobile bar settings (neutral default values)
    const stickyEnabled: boolean = settings.stickyMobileBar === true;
    const stickyMode: 'always' | 'scroll' = settings.stickyMobileBarMode || 'always';
    const [isScrolledPast, setIsScrolledPast] = useState(false);

    useEffect(() => {
        if (!stickyEnabled || stickyMode !== 'scroll' || isAdmin) return;
        const target = mainButtonRef.current;
        if (!target) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                // When original button is scrolled past top of viewport
                setIsScrolledPast(!entry.isIntersecting && entry.boundingClientRect.top < 0);
            },
            { threshold: 0.1 }
        );
        observer.observe(target);
        return () => observer.disconnect();
    }, [stickyEnabled, stickyMode, isAdmin]);

    const stickyBgColor: string = settings.stickyBgColor || 'rgba(255, 255, 255, 0.95)';
    const stickyBorderColor: string = settings.stickyBorderColor || '#e5e7eb';
    const stickyPaddingY: number = settings.stickyPaddingY ?? 12;
    const stickyPaddingX: number = settings.stickyPaddingX ?? 20;
    const stickyBackdropBlur: boolean = settings.stickyBackdropBlur !== false;

    const stickyPriceColor: string = settings.stickyPriceColor || '#111827';
    const stickyPriceFontSize: number = settings.stickyPriceFontSize ?? 20;
    const stickyPriceFontWeight: string = settings.stickyPriceFontWeight || '800';

    const stickySubtitleColor: string = settings.stickySubtitleColor || '#6b7280';
    const stickySubtitleFontSize: number = settings.stickySubtitleFontSize ?? 12;
    const stickySubtitleFontWeight: string = settings.stickySubtitleFontWeight || 'normal';

    const stickyBtnBgColor: string = settings.stickyBtnBgColor || settings.btnBgColor || '#2563eb';
    const stickyBtnTextColor: string = settings.stickyBtnTextColor || settings.btnTextColor || '#ffffff';
    const stickyBtnBorderRadius: number = settings.stickyBtnBorderRadius ?? 9999;
    const stickyBtnPaddingY: number = settings.stickyBtnPaddingY ?? 14;
    const stickyBtnPaddingX: number = settings.stickyBtnPaddingX ?? 28;
    const stickyBtnFontSize: number = settings.stickyBtnFontSize ?? 15;
    const stickyBtnFontWeight: string = settings.stickyBtnFontWeight || 'bold';

    const buttonsOrder: string[] = Array.isArray(settings.buttonsOrder) && settings.buttonsOrder.length > 0
        ? settings.buttonsOrder
        : ['addToCart', 'buyNow', 'negotiate'];

    if (isAdmin) {
        const demoPrice = activeProduct?.price || 129;
        const demoSubtitle = settings.stickySubtitleText?.trim() || activeProduct?.name || 'Produkt';
        const demoBtnText = settings.stickyButtonText?.trim() || buttonText || 'Dodaj do koszyka';

        return (
            <div className="relative w-full max-w-full" style={wrapperStyle}>
                <style>{hoverStyle}</style>
                <DemoBadge />
                <div style={buttonsRowStyle} className="max-w-full">
                    {buttonsOrder.map((key) => {
                        if (key === 'addToCart' && showAddToCart) {
                            return (
                                <Button
                                    key="addToCart"
                                    size="lg"
                                    style={{ ...buttonStyle, ...hoverEffectVars, maxWidth: '100%', boxSizing: 'border-box' }}
                                    className={cn(cls, hoverEffectClass, 'gap-2 max-w-full', fullWidth && 'w-full')}
                                    onClick={() => {
                                        if (settings.useAddToCartModal !== false) {
                                            setShowAddToCartModal(true);
                                        }
                                    }}
                                >
                                    {btnIconPosition === 'left' && btnIconEl}
                                    {buttonText}
                                    {btnIconPosition === 'right' && btnIconEl}
                                </Button>
                            );
                        }
                        if (key === 'buyNow' && showBuyNow) {
                            return (
                                <Button
                                    key="buyNow"
                                    size="lg"
                                    style={{ ...buyNowButtonStyle, ...hoverEffectVars, maxWidth: '100%', boxSizing: 'border-box' }}
                                    className={cn(buyNowCls, hoverEffectClass, 'gap-2 max-w-full', fullWidth && 'w-full')}
                                >
                                    {buyNowIconPosition === 'left' && buyNowIconEl}
                                    {buyNowText}
                                    {buyNowIconPosition === 'right' && buyNowIconEl}
                                </Button>
                            );
                        }
                        if (key === 'negotiate' && showNegotiate) {
                            return (
                                <Button
                                    key="negotiate"
                                    size="lg"
                                    style={{ ...negotiateButtonStyle, ...hoverEffectVars, maxWidth: '100%', boxSizing: 'border-box' }}
                                    className={cn(negotiateCls, hoverEffectClass, 'gap-2 max-w-full', fullWidth && 'w-full')}
                                >
                                    {negotiateIconPosition === 'left' && negotiateIconEl}
                                    {negotiateText}
                                    {negotiateIconPosition === 'right' && negotiateIconEl}
                                </Button>
                            );
                        }
                        return null;
                    })}
                </div>
                {showAddToCartModal && (
                    <AddToCartModal
                        isOpen={showAddToCartModal}
                        onClose={() => setShowAddToCartModal(false)}
                        addedProduct={{
                            name: activeProduct?.name || t('product.sample_name', 'Przykładowy produkt'),
                            price: activeProduct?.price || 129,
                            image_url: null,
                        }}
                        modalTitle={settings.atcModalTitle}
                        modalBrand={settings.atcModalBrand}
                        recommendationsHeader={settings.atcRecommendationsHeader}
                        recommendationsQuery={{
                            source: settings.atcRecommendationsSource || 'auto',
                            manualProductIds: settings.atcManualProductIds,
                            categoryId: settings.atcCategoryId,
                            limit: settings.atcRecommendationsLimit ?? 4,
                            excludeProductId: activeProduct?.id,
                        }}
                        continueShoppingText={settings.atcContinueShoppingText}
                        checkoutText={settings.atcCheckoutText}
                        style={{
                            accentColor: settings.atcAccentColor,
                            primaryBg: settings.atcPrimaryBg,
                            primaryText: settings.atcPrimaryText,
                            secondaryBorderColor: settings.atcSecondaryBorderColor,
                            secondaryTextColor: settings.atcSecondaryTextColor,
                            borderRadius: settings.atcBorderRadius,
                        }}
                    />
                )}
            </div>
        );
    }

    if (!activeProduct) return null;

    // "Cena na zapytanie": nothing to put in a cart — the page's own contact buttons carry the action.
    if (activeProduct.price_on_request) return null;

    // A Pick & Pack box is only purchasable with its parts chosen — a bare "Dodaj do koszyka" here
    // used to put an empty box in the cart. Render the configurator for this product instead.
    if (activeProduct.product_type === 'pick_pack') {
        return (
            <PickPackBlock
                {...blockProps}
                block={{ id: `${block.id}-pickpack`, type: 'pick-pack', settings: { productId: activeProduct.id }, styles: {} } as any}
                currentStyles={{}}
                isAdmin={isAdmin}
                viewport={viewport}
            />
        );
    }

    // Digital/virtual products (requires_shipping=false) have no meaningful stock —
    // they're delivered as a file/license code, not a finite physical unit. Services
    // (product_type === 'service') are the same: door-to-door usage isn't a finite
    // stocked unit either — backend create_order skips stock decrement for them too.
    const isDigital = activeProduct.requires_shipping === false;
    const isService = activeProduct.product_type === 'service';
    // A plan is bought through Stripe Billing (POST /subscriptions/checkout), never through the cart
    // — the cart would carry it at its 0 zł order price. A flat-rate service is one charge, so it
    // is capped at one line of quantity 1 like a one-off product.
    const isSubscriptionPlan = activeProduct.product_type === 'subscription_plan';
    const isFlatService = isService && activeProduct.service_config?.pricing_type === 'flat';
    const isOutOfStock = !isDigital && !isService && (activeVariant
        ? (activeVariant.stock <= 0)
        : (activeProduct.stock <= 0));
    // "Na zamówienie" — stock_zero_action="allow" (set per-product in admin's Magazyn tab,
    // or per-shop default in Ustawienia) keeps checkout open past zero stock. "hide"/"label"
    // still block the purchase here, matching the same policy enforced in create_order.
    const isBackorder = isOutOfStock && activeProduct.stock_zero_action === 'allow';
    const isPurchaseBlocked = isOutOfStock && !isBackorder;

    // Falls back to 1 when no product-quantity block is on the page — quantity then
    // stays an implementation detail merchants who don't add that block never see.
    // One-off products are capped at 1 regardless of the picked quantity — they can
    // only ever be bought once, so "3x" would misrepresent what's being purchased.
    // A service's minimum load ("min. 4 kg") is the least that goes in, whatever was picked.
    const minLoad = serviceMinQuantity(activeProduct);
    // Per m²: one line per measured piece size; nothing to add until the dimensions are in.
    const isAreaService = servicePricingType(activeProduct) === 'area';
    // A service on a template without a product-quantity block gets its own field here (weight!).
    const showInlineQuantity = isService && !isFlatService && !usesConfigurator(activeProduct) && !productState?.pageControls?.quantity;
    const measure = isAreaService ? productState?.serviceMeasure ?? null : null;
    const quantity = activeProduct.is_one_off || isFlatService || isSubscriptionPlan ? 1 : Math.max(minLoad, productState?.quantity || 1);
    // The configurator adds its own line (with the chosen options) and shows its own toast.
    const viaConfigurator = usesConfigurator(activeProduct) && !!productState?.configurableAddToCart;

    const addItemToCart = (): boolean => {
        if (viaConfigurator) {
            return productState!.configurableAddToCart!();
        }
        try {
            if (isAreaService && !measure) {
                toast.error(t('product.measure_required', 'Podaj wymiary, żeby dodać usługę do koszyka.'));
                return false;
            }
            const cart = JSON.parse(localStorage.getItem('stag-cart') || '[]');
            const cartItemId = activeVariant
                ? `${activeProduct.id}-${activeVariant.id}`
                : measure ? `${activeProduct.id}-m${measure.width_cm}x${measure.length_cm}` : `${activeProduct.id}`;
            const existingItem = cart.find((item: any) => item.cartItemId === cartItemId);

            if (existingItem) {
                if (activeProduct.is_one_off || isFlatService) {
                    toast.error(t('product.one_off_limit', 'Ten produkt można dodać do koszyka tylko raz.'));
                    return false;
                }
                existingItem.quantity += quantity;
                Object.assign(existingItem, applyB2BTiers(existingItem));
            } else {
                const activePrice = activeVariant
                    ? (activeVariant.sales_price !== null && activeVariant.sales_price !== undefined ? activeVariant.sales_price : activeVariant.price)
                    : (activeProduct.sales_price !== null && activeProduct.sales_price !== undefined ? activeProduct.sales_price : activeProduct.price);
                // Progi B2B (D9): koszyk przelicza cenę linii z tej ceny bazowej po każdej zmianie ilości.
                const b2bTiers = measure ? null : (activeVariant || activeProduct).b2b_tiers;
                const mainImage = activeProduct.images?.find((img: any) => img.is_main) || activeProduct.images?.[0];
                // The variant's own photo (e.g. the green one), so the cart shows what was picked.
                const imagePath = activeVariant?.image_path || mainImage?.path;
                const imageUrl = imagePath ? getAbsoluteMediaUrl(imagePath) : null;

                const variantName = activeVariant
                    ? ` (${Object.values(activeVariant.attributes).join(', ')})`
                    : measure ? ` (${measure.width_cm} × ${measure.length_cm} cm)` : '';

                cart.push({
                    cartItemId,
                    id: activeProduct.id,
                    variant_id: activeVariant?.id || null,
                    name: activeProduct.name + variantName,
                    // Per m²: the price of this piece (rate × billable area) — create_order prices it the same way.
                    price: measure ? Math.round(activePrice * serviceBillableArea(activeProduct, measure) * 100) / 100 : activePrice,
                    image: imageUrl,
                    slug: activeProduct.slug,
                    quantity,
                    attributes: activeVariant?.attributes || null,
                    is_one_off: !!activeProduct.is_one_off,
                    // Read by CheckoutBlock/CartBlock for per-type branching (digital shipping
                    // skip, service mixed-cart rule, local_fleet delivery method) — was missing
                    // here entirely, so those checks always saw `undefined` for every product
                    // added via this button (the main product-page "buy" button).
                    product_type: activeProduct.product_type,
                    requires_shipping: activeProduct.requires_shipping,
                    // The cart's "−" stops here (CartBlock.updateQuantity).
                    ...(minLoad > 1 ? { min_quantity: minLoad } : {}),
                    ...(measure ? { service_measure: measure } : {}),
                    ...(b2bTiers?.length ? { b2b_base_price: activePrice, b2b_tiers: b2bTiers, price: b2bTierPrice(activePrice, b2bTiers, quantity) } : {}),
                    tax_rate: activeProduct.tax_rate ?? 23,
                });
            }

            localStorage.setItem('stag-cart', JSON.stringify(cart));
            window.dispatchEvent(new Event('cart-updated'));

            // Track AddToCart (Google dataLayer + Meta Pixel)
            trackAddToCart(activeProduct, activeVariant, quantity, currency || 'PLN');
            return true;
        } catch {
            toast.error(t('product.add_to_cart_error'));
            return false;
        }
    };

    const addToCart = () => {
        if (!addItemToCart()) return;
        setIsAdded(true);
        setTimeout(() => setIsAdded(false), 2000);
        if (settings.useAddToCartModal !== false) {
            setShowAddToCartModal(true);
        } else if (!viaConfigurator) {
            toast.success(t('product.add_to_cart_success'));
        }
    };

    const buyNow = () => {
        if (!addItemToCart()) return;
        router.push(tPath('/zamowienie'));
    };

    const negotiateCatalogPrice = activeVariant
        ? (activeVariant.sales_price !== null && activeVariant.sales_price !== undefined ? activeVariant.sales_price : activeVariant.price)
        : (activeProduct.sales_price !== null && activeProduct.sales_price !== undefined ? activeProduct.sales_price : activeProduct.price);

    const handleNegotiateClick = () => {
        if (!session?.user) {
            setShowLoginGate(true);
            return;
        }
        setShowNegotiateModal(true);
    };

    const isDisabled = isPurchaseBlocked || (activeProduct?.is_one_off && isInCart) || (isAreaService && !measure);
    // A `service`'s own "subscribe and save" tiers (subscription_plan has no tiers of its own to
    // check here — see hasBaseOption in SubscriptionPurchasePanel — its single "no commitment"
    // option is the plan's own fixed_price, always offered).
    const serviceSubscriptionTiers = isService ? activeSubscriptionTiers(activeProduct.service_config) : [];

    const stickyPrice = activeVariant
        ? (activeVariant.sales_price !== null && activeVariant.sales_price !== undefined ? activeVariant.sales_price : activeVariant.price)
        : (activeProduct.sales_price !== null && activeProduct.sales_price !== undefined ? activeProduct.sales_price : activeProduct.price);
    const stickySubtitle = settings.stickySubtitleText?.trim() || activeProduct.name || '';
    const stickyButtonText = settings.stickyButtonText?.trim() || (isBackorder ? backorderButtonText : buttonText) || t('product.add_to_cart');
    // The sticky mobile bar is a one-tap shortcut for the ordinary one-off purchase — a plan has
    // no such single action (it always needs a tier/payment-mode choice), so it stays hidden there.
    const isStickyVisible = !isSubscriptionPlan && (stickyMode === 'always' || isScrolledPast);

    return (
        <div ref={mainButtonRef} className={cn('relative w-full max-w-full', `atc-wrap-${safeBlockId}`)} style={isAreaService || showInlineQuantity ? { ...wrapperStyle, flexWrap: 'wrap' } : wrapperStyle}>
            <style>{hoverStyle}</style>
            {!isAdmin && showLoginGate && (
                <LoginGateModal isOpen={showLoginGate} onClose={() => setShowLoginGate(false)} />
            )}
            {!isAdmin && showNegotiateModal && (
                <>
                    <LoginGateModal isOpen={showLoginGate} onClose={() => setShowLoginGate(false)} />
                    <NegotiatePriceModal
                        isOpen={showNegotiateModal}
                        onClose={() => setShowNegotiateModal(false)}
                        productId={activeProduct.id}
                        catalogPrice={negotiateCatalogPrice || 0}
                        currency={currency || 'PLN'}
                        quantity={quantity}
                    />
                </>
            )}
            {/* A plan is bought through SubscriptionPurchasePanel below, never through these
                buttons — see the 2026-09-22 add-to-cart-surface audit (a plan quick-added here
                would carry its 0 zł order price and land in a normal cart/checkout that then
                rejects it at the very last step). */}
            {isAreaService && (
                <div className="w-full mt-2 mb-4">
                    <ServiceMeasureFields product={activeProduct} />
                </div>
            )}
            {showInlineQuantity && (
                <div className="w-full mb-4">
                    <InlineServiceQuantity product={activeProduct} />
                </div>
            )}
            {!isSubscriptionPlan && (
                <div style={buttonsRowStyle} className="max-w-full">
                    {buttonsOrder.map((key) => {
                        if (key === 'addToCart' && showAddToCart) {
                            return (
                                <Button
                                    key="addToCart"
                                    size="lg"
                                    style={{ ...buttonStyle, ...hoverEffectVars, maxWidth: '100%', boxSizing: 'border-box' }}
                                    className={cn(cls, hoverEffectClass, 'gap-2 max-w-full', fullWidth && 'w-full')}
                                    onClick={addToCart}
                                    disabled={isDisabled}
                                >
                                    {btnIconPosition === 'left' && btnIconEl}
                                    {isAdded
                                        ? t('product.added')
                                        : (activeProduct?.is_one_off && isInCart
                                            ? t('product.already_in_cart', 'W koszyku')
                                            : (isBackorder ? backorderButtonText : buttonText))}
                                    {btnIconPosition === 'right' && btnIconEl}
                                </Button>
                            );
                        }
                        if (key === 'buyNow' && showBuyNow) {
                            return (
                                <Button
                                    key="buyNow"
                                    size="lg"
                                    style={{ ...buyNowButtonStyle, ...hoverEffectVars, maxWidth: '100%', boxSizing: 'border-box' }}
                                    className={cn(buyNowCls, hoverEffectClass, 'gap-2 max-w-full', fullWidth && 'w-full')}
                                    onClick={buyNow}
                                    disabled={isDisabled}
                                >
                                    {buyNowIconPosition === 'left' && buyNowIconEl}
                                    {isBackorder ? backorderButtonText : buyNowText}
                                    {buyNowIconPosition === 'right' && buyNowIconEl}
                                </Button>
                            );
                        }
                        if (key === 'negotiate' && showNegotiate) {
                            return (
                                <Button
                                    key="negotiate"
                                    size="lg"
                                    style={{ ...negotiateButtonStyle, ...hoverEffectVars, maxWidth: '100%', boxSizing: 'border-box' }}
                                    className={cn(negotiateCls, hoverEffectClass, 'gap-2 max-w-full', fullWidth && 'w-full')}
                                    onClick={handleNegotiateClick}
                                    disabled={isPurchaseBlocked}
                                >
                                    {negotiateIconPosition === 'left' && negotiateIconEl}
                                    {negotiateText}
                                    {negotiateIconPosition === 'right' && negotiateIconEl}
                                </Button>
                            );
                        }
                        return null;
                    })}
                </div>
            )}

            {isSubscriptionPlan && (
                <SubscriptionPurchasePanel
                    product={activeProduct}
                    subscribeButtonText={settings.subscribeButtonText}
                    buttonStyle={{ ...buttonStyle, ...hoverEffectVars }}
                    buttonClassName={cn(cls, hoverEffectClass, fullWidth && 'w-full')}
                />
            )}
            {!isSubscriptionPlan && serviceSubscriptionTiers.length > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-100 space-y-2">
                    <p className="text-sm font-semibold text-slate-700">
                        {t('product.subscription_or_subscribe', 'Albo zasubskrybuj i zaoszczędź')}
                    </p>
                    <SubscriptionPurchasePanel product={activeProduct} subscribeButtonText={settings.subscribeButtonText} />
                </div>
            )}

            {/* Sticky Mobile Add To Cart Bar (rendered on live storefront) */}
            {stickyEnabled && (
                <div
                    className={cn(
                        'fixed inset-x-0 bottom-0 z-40 md:hidden flex items-center justify-between gap-3 border-t transition-transform duration-300 ease-out',
                        stickyBackdropBlur && 'backdrop-blur-md',
                        isStickyVisible ? 'translate-y-0' : 'translate-y-full pointer-events-none'
                    )}
                    style={{
                        backgroundColor: stickyBgColor,
                        borderColor: stickyBorderColor,
                        padding: `${stickyPaddingY}px ${stickyPaddingX}px`,
                        boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.06)',
                    }}
                >
                    <div className="leading-tight min-w-0 flex-1 pr-2">
                        <div
                            className="truncate"
                            style={{
                                color: stickyPriceColor,
                                fontSize: `${stickyPriceFontSize}px`,
                                fontWeight: stickyPriceFontWeight,
                            }}
                        >
                            {stickyPrice !== undefined && stickyPrice !== null ? stickyPrice : '—'}&nbsp;{currency || 'zł'}
                        </div>
                        {stickySubtitle && (
                            <div
                                className="truncate"
                                style={{
                                    color: stickySubtitleColor,
                                    fontSize: `${stickySubtitleFontSize}px`,
                                    fontWeight: stickySubtitleFontWeight,
                                }}
                            >
                                {stickySubtitle}
                            </div>
                        )}
                    </div>
                    <div className="contents">
                        <button
                            type="button"
                            onClick={addToCart}
                            disabled={isDisabled}
                            className="shrink-0 whitespace-nowrap transition active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
                            style={{
                                backgroundColor: stickyBtnBgColor,
                                color: stickyBtnTextColor,
                                borderRadius: `${stickyBtnBorderRadius}px`,
                                padding: `${stickyBtnPaddingY}px ${stickyBtnPaddingX}px`,
                                fontSize: `${stickyBtnFontSize}px`,
                                fontWeight: stickyBtnFontWeight,
                            }}
                        >
                            {isAdded
                                ? t('product.added')
                                : (activeProduct?.is_one_off && isInCart
                                    ? t('product.already_in_cart', 'W koszyku')
                                    : stickyButtonText)}
                        </button>
                    </div>
                </div>
            )}

            <AddToCartModal
                isOpen={showAddToCartModal}
                onClose={() => setShowAddToCartModal(false)}
                addedProduct={{
                    name: activeProduct.name,
                    price: activeProduct.price,
                }}
                modalTitle={settings.atcModalTitle}
                modalBrand={settings.atcModalBrand}
                recommendationsHeader={settings.atcRecommendationsHeader}
                continueShoppingText={settings.atcContinueShoppingText}
                checkoutText={settings.atcCheckoutText}
                recommendationsQuery={{
                    source: settings.atcRecommendationsSource || 'auto',
                    manualProductIds: settings.atcManualProductIds,
                    categoryId: settings.atcCategoryId,
                    limit: settings.atcRecommendationsLimit,
                    excludeProductId: activeProduct.id,
                }}
                style={{
                    accentColor: settings.atcAccentColor,
                    primaryBg: settings.atcPrimaryBg,
                    primaryText: settings.atcPrimaryText,
                    secondaryBorderColor: settings.atcSecondaryBorderColor,
                    secondaryTextColor: settings.atcSecondaryTextColor,
                    borderRadius: settings.atcBorderRadius,
                }}
            />
        </div>
    );
}

// ─── product-stock ───────────────────────────────────────────────────────────

export function ProductStockBlock({ block, currentStyles, isAdmin, product }: BlockProps) {
    const { t } = useTranslation();
    const productState = useProductState();
    const data = productState?.product || product || (isAdmin ? DEMO_PRODUCT : null);
    if (!data) return null;

    const activeVariant = productState?.selectedVariant;
    const stock = activeVariant ? (activeVariant.stock ?? 0) : (data.stock ?? 0);
    const displayMode: 'hidden' | 'text' | 'dot' | 'bar' = block.settings?.displayMode || 'dot';
    const maxStock = block.settings?.maxStock || 100;
    // "Na zamówienie" — same stock_zero_action="allow" policy the buy button reads, so both
    // blocks agree on when a zero-stock product is still being sold vs. genuinely unavailable.
    const isBackorder = stock <= 0 && data.stock_zero_action === 'allow';

    // Digital/virtual products and services have no meaningful stock concept — don't show as "out of stock".
    if (displayMode === 'hidden' || data.requires_shipping === false || data.product_type === 'service') return null;

    return (
        <div className={isAdmin ? 'relative' : ''}>
            {isAdmin && <DemoBadge />}
            {displayMode === 'text' && (
                <p className="text-sm text-slate-600">
                    {stock > 0
                        ? t('product.in_stock').replace('{n}', String(stock))
                        : (isBackorder ? t('product.backorder', 'Na zamówienie') : t('product.out_of_stock'))}
                </p>
            )}
            {displayMode === 'bar' && (
                <div className="space-y-1">
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                            className={`h-full rounded-full transition-all ${stock > 0 ? 'bg-green-500' : isBackorder ? 'bg-amber-400' : 'bg-red-400'}`}
                            style={{ width: `${stock > 0 ? Math.min((stock / maxStock) * 100, 100) : 100}%` }}
                        />
                    </div>
                    <p className="text-xs text-slate-500">
                        {stock > 0 ? t('product.in_stock_count', 'Na stanie: {n}').replace('{n}', String(stock)) : (isBackorder ? t('product.backorder', 'Na zamówienie') : t('product.out_of_stock', 'Brak w magazynie'))}
                    </p>
                </div>
            )}
            {displayMode === 'dot' && (
                <div className="flex items-center gap-2">
                    {stock > 0 ? (
                        <>
                            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                            <p className="text-sm text-green-700 font-semibold">{t('product.available', 'Dostępny')}</p>
                        </>
                    ) : isBackorder ? (
                        <>
                            <div className="w-2 h-2 rounded-full bg-amber-500" />
                            <p className="text-sm text-amber-700 font-semibold">{t('product.backorder', 'Na zamówienie')}</p>
                        </>
                    ) : (
                        <>
                            <div className="w-2 h-2 rounded-full bg-red-500" />
                            <p className="text-sm text-red-600 font-bold">{t('product.unavailable', 'Niedostępny')}</p>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── Unified dispatcher ──────────────────────────────────────────────────────

import { ProductReviews, RatingDisplay, formatReviewCount } from '@/modules/shop/components/product/product-reviews';
import type { RatingSymbol } from '@/modules/shop/components/product/product-reviews';

export function ProductReviewsBlock({ block, currentStyles, isAdmin, product }: BlockProps) {
    const data = product || (isAdmin ? DEMO_PRODUCT : null);
    const siteConfig = useSiteConfig();

    if (!data) return null;

    // Storefront: if allow_reviews is explicitly false, hide it
    if (!isAdmin && !siteConfig.allow_reviews) {
        return null;
    }

    const settings = {
        layout: block.settings?.layout || 'list',
        ratingSymbol: block.settings?.ratingSymbol || 'star',
        ratingColor: block.settings?.ratingColor || '#f59e0b',
        cardBackground: block.settings?.cardBackground || '#ffffff',
        cardBorderColor: block.settings?.cardBorderColor || '#f1f5f9',
        cardBorderRadius: currentStyles.cardBorderRadius ?? block.settings?.cardBorderRadius ?? 16,
        accentColor: block.settings?.accentColor || '',
        showDivider: block.settings?.showDivider !== false,
        // These 3 have real, working editor controls (ProductReviewsEditor.tsx) but were
        // never actually passed through to <ProductReviews> — the button color/radius
        // settings were completely inert no matter what a merchant configured.
        buttonBg: block.settings?.buttonBg || undefined,
        buttonTextColor: block.settings?.buttonTextColor || '#ffffff',
        buttonBorderRadius: currentStyles.buttonBorderRadius ?? block.settings?.buttonBorderRadius ?? 12,
    };

    return (
        <div className={isAdmin ? 'relative' : ''}>
            {isAdmin && <DemoBadge />}
            {isAdmin && !siteConfig.allow_reviews && (
                <div className="bg-amber-50 border border-dashed border-amber-300 text-amber-800 p-4 rounded-xl mb-4 text-sm font-medium">
                    ⚠️ Opinie są wyłączone w konfiguracji sklepu. Ten blok nie będzie widoczny dla klientów.
                </div>
            )}
            <ProductReviews productId={data.id} siteConfig={siteConfig} settings={settings} />
        </div>
    );
}

// ─── product-rating-mini ─────────────────────────────────────────────────────

export function ProductRatingMiniBlock({ block, currentStyles, isAdmin, product }: BlockProps) {
    const productState = useProductState();
    const data = productState?.product || product || (isAdmin ? DEMO_PRODUCT : null);
    const { locale } = useTranslation();

    const [avgRating, setAvgRating] = useState(isAdmin ? 4.2 : 0);
    const [reviewCount, setReviewCount] = useState(isAdmin ? 12 : 0);
    const siteConfig = useSiteConfig();

    const settings = block.settings || {};
    const ratingSymbol: RatingSymbol = settings.ratingSymbol ?? 'star';
    const ratingColor: string = settings.ratingColor ?? '#f59e0b';
    const size: 'sm' | 'md' | 'lg' = currentStyles?.size ?? settings.size ?? 'md';
    const showCount: boolean = settings.showCount !== false;
    const showValue: boolean = settings.showValue === true;
    const textFontSize = currentStyles?.textFontSize ?? settings.textFontSize;

    const textStyle: React.CSSProperties = {
        fontFamily: settings.textFontFamily && settings.textFontFamily !== 'inherit' ? settings.textFontFamily : undefined,
        fontWeight: settings.textFontWeight || undefined,
        fontSize: textFontSize ? `${textFontSize}px` : undefined,
        color: settings.textColor || undefined,
    };

    useEffect(() => {
        if (isAdmin || !data?.id) return;
        fetchAPI(`/reviews/product/${data.id}`)
            .then((reviews: any) => {
                if (Array.isArray(reviews) && reviews.length > 0) {
                    const avg = reviews.reduce((a: number, r: any) => a + r.rating, 0) / reviews.length;
                    setAvgRating(avg);
                    setReviewCount(reviews.length);
                }
            })
            .catch(() => {});
    }, [data?.id, isAdmin]);

    if (!data) return null;

    // Storefront: if allow_reviews is explicitly false, hide it
    if (!isAdmin && !siteConfig.allow_reviews) {
        return null;
    }

    if (!isAdmin && reviewCount === 0) return null;

    return (
        <div className={isAdmin ? 'relative' : ''} style={currentStyles}>
            {isAdmin && <DemoBadge />}
            {isAdmin && !siteConfig.allow_reviews && (
                <div className="text-[10px] text-amber-600 font-bold bg-amber-50 border border-amber-200 px-2 py-1 rounded inline-block mb-1">
                    ⚠️ Ukryte na sklepie (opinie wyłączone)
                </div>
            )}
            <div className="flex items-center gap-2">
                <RatingDisplay value={avgRating} symbol={ratingSymbol} color={ratingColor} size={size} />
                {showValue && <span className="text-sm font-bold text-slate-700" style={textStyle}>{avgRating.toFixed(1)}</span>}
                {showCount && <span className="text-sm text-slate-500" style={textStyle}>{formatReviewCount(reviewCount, locale)}</span>}
            </div>
        </div>
    );
}

// ─── product-condition ───────────────────────────────────────────────────────

const DEMO_PRODUCT_WITH_CONDITION = { ...DEMO_PRODUCT, condition: 'used' };

export function ProductConditionBlock({ block, currentStyles, isAdmin, product }: BlockProps) {
    const { t } = useTranslation();
    const productState = useProductState();

    // In admin preview, use DEMO_PRODUCT with 'used' so the badge renders visibly
    // In storefront, use context product (has real condition from API)
    const data = isAdmin
        ? (productState?.product ?? DEMO_PRODUCT_WITH_CONDITION)
        : (productState?.product || product || null);

    if (!data) return null;
    // New / used describes goods — a service (laundry, repair) has no such condition.
    if (!isAdmin && (data.product_type === 'service' || data.product_type === 'subscription_plan')) return null;

    const selectedVariant = productState?.selectedVariant;
    // Variant condition overrides product condition; null falls back via ??
    const effectiveCondition: string | null = (selectedVariant?.condition) ?? data.condition ?? null;

    const settings = block.settings || {};
    const hideIfNoCondition = settings.hideIfNoCondition !== false;
    if (!effectiveCondition && hideIfNoCondition && !isAdmin) return null;

    const condition = effectiveCondition || (isAdmin ? 'used' : null);
    if (!condition) return null;

    // Badge appearance settings
    const align: 'left' | 'center' | 'right' = settings.align || 'left';
    const alignClass = align === 'center' ? 'justify-center' : align === 'right' ? 'justify-end' : 'justify-start';

    const shape: 'pill' | 'rounded' | 'square' = settings.shape || 'pill';
    const radiusClass = shape === 'pill' ? 'rounded-full' : shape === 'rounded' ? 'rounded-lg' : 'rounded-md';

    let bgColor = '#22c55e';
    let textColor = '#ffffff';
    let borderWidth = 0;
    let borderColor = '#16a34a';
    let label = t('product.condition_new', 'Nowy');

    if (condition === 'used') {
        bgColor = settings.usedBgColor || '#f59e0b';
        textColor = settings.usedTextColor || '#ffffff';
        borderWidth = settings.usedBorderWidth ?? 0;
        borderColor = settings.usedBorderColor || '#d97706';
        label = settings.usedLabel || t('product.condition_used', 'Używany');
    } else if (condition === 'poekspozycyjny') {
        bgColor = settings.poekspozycyjnyBgColor || '#3b82f6';
        textColor = settings.poekspozycyjnyTextColor || '#ffffff';
        borderWidth = settings.poekspozycyjnyBorderWidth ?? 0;
        borderColor = settings.poekspozycyjnyBorderColor || '#2563eb';
        label = settings.poekspozycyjnyLabel || t('product.condition_poekspozycyjny', 'Poekspozycyjny');
    } else if (condition === 'powystawowy') {
        bgColor = settings.powystawowyBgColor || '#6366f1';
        textColor = settings.powystawowyTextColor || '#ffffff';
        borderWidth = settings.powystawowyBorderWidth ?? 0;
        borderColor = settings.powystawowyBorderColor || '#4f46e5';
        label = settings.powystawowyLabel || t('product.condition_powystawowy', 'Powystawowy');
    } else if (condition === 'uszkodzony') {
        bgColor = settings.uszkodzonyBgColor || '#ef4444';
        textColor = settings.uszkodzonyTextColor || '#ffffff';
        borderWidth = settings.uszkodzonyBorderWidth ?? 0;
        borderColor = settings.uszkodzonyBorderColor || '#dc2626';
        label = settings.uszkodzonyLabel || t('product.condition_uszkodzony', 'Uszkodzony');
    } else if (condition === 'odnowiony') {
        bgColor = settings.odnowionyBgColor || '#06b6d4';
        textColor = settings.odnowionyTextColor || '#ffffff';
        borderWidth = settings.odnowionyBorderWidth ?? 0;
        borderColor = settings.odnowionyBorderColor || '#0891b2';
        label = settings.odnowionyLabel || t('product.condition_odnowiony', 'Odnowiony');
    } else if (condition === 'zwrot') {
        bgColor = settings.zwrotBgColor || '#8b5cf6';
        textColor = settings.zwrotTextColor || '#ffffff';
        borderWidth = settings.zwrotBorderWidth ?? 0;
        borderColor = settings.zwrotBorderColor || '#7c3aed';
        label = settings.zwrotLabel || t('product.condition_zwrot', 'Zwrot konsumencki');
    } else if (condition === 'outlet') {
        bgColor = settings.outletBgColor || '#ec4899';
        textColor = settings.outletTextColor || '#ffffff';
        borderWidth = settings.outletBorderWidth ?? 0;
        borderColor = settings.outletBorderColor || '#db2777';
        label = settings.outletLabel || t('product.condition_outlet', 'Outlet');
    } else { // 'new'
        bgColor = settings.newBgColor || '#22c55e';
        textColor = settings.newTextColor || '#ffffff';
        borderWidth = settings.newBorderWidth ?? 0;
        borderColor = settings.newBorderColor || '#16a34a';
        label = settings.newLabel || t('product.condition_new', 'Nowy');
    }

    const fontSize = currentStyles.fontSize ?? settings.fontSize ?? 11;
    const fontWeight = settings.fontWeight || '800';
    const fontFamily = settings.fontFamily && settings.fontFamily !== 'inherit' ? settings.fontFamily : undefined;
    const letterSpacingVal = currentStyles.letterSpacing ?? settings.letterSpacing;
    const letterSpacing = letterSpacingVal != null ? `${letterSpacingVal}px` : '3px';
    const paddingX = currentStyles.paddingX ?? settings.paddingX ?? 12;
    const paddingY = currentStyles.paddingY ?? settings.paddingY ?? 4;

    const badgeStyle: React.CSSProperties = {
        backgroundColor: bgColor,
        color: textColor,
        borderWidth: borderWidth ? `${borderWidth}px` : undefined,
        borderStyle: borderWidth ? 'solid' : undefined,
        borderColor: borderWidth ? borderColor : undefined,
        fontSize: `${fontSize}px`,
        fontWeight,
        fontFamily,
        letterSpacing,
        paddingLeft: `${paddingX}px`,
        paddingRight: `${paddingX}px`,
        paddingTop: `${paddingY}px`,
        paddingBottom: `${paddingY}px`,
        transition: 'all 0.3s',
        display: 'inline-block',
        textTransform: 'uppercase',
    };

    return (
        <div className={isAdmin ? 'relative' : ''}>
            {isAdmin && <DemoBadge />}
            <div className={`flex ${alignClass}`} style={currentStyles}>
                <span className={`product-condition-wrapper shadow-sm ${radiusClass}`} style={badgeStyle}>
                    {label}
                </span>
            </div>
        </div>
    );
}

// ─── product-spec ────────────────────────────────────────────────────────────

const DEFAULT_SPECS_DEMO = [
    {
        category: 'Osiągi i Napęd',
        items: [
            { name: 'Moc nominalna', value: '3000 W' },
            { name: 'Moc szczytowa', value: '5600 W' },
            { name: 'Prędkość maksymalna', value: '80 km/h' },
            { name: 'Zasięg', value: 'do 100 km' },
        ],
    },
    {
        category: 'Wymiary i Masa',
        items: [
            { name: 'Masa', value: '78 kg' },
            { name: 'Wymiary (dł./szer./wys.)', value: '1850 / 780 / 1150 mm' },
            { name: 'Gwarancja', value: '24 miesiące' },
        ],
    },
];

type SpecCategory = { category: string; items: { name: string; value: string }[] };

function parseSpecifications(flatSpecs: { name: string; value: string }[]): SpecCategory[] {
    const categories: SpecCategory[] = [];
    let currentCategory: SpecCategory = { category: 'Parametry Ogólne', items: [] };

    flatSpecs.forEach((item) => {
        // Some legacy migrated rows store the label under `key` instead of `name`.
        const itemName = item?.name ?? (item as any)?.key ?? '';
        // A section header is ONLY when name is wrapped in [brackets]
        const isSection = itemName.startsWith('[') && itemName.endsWith(']');
        if (isSection) {
            const catName = itemName.replace(/^\[|\]$/g, '').trim();
            if (currentCategory.items.length > 0) {
                categories.push(currentCategory);
            }
            currentCategory = { category: catName || 'Inne', items: [] };
        } else if (itemName.trim() !== '') {
            currentCategory.items.push({ name: itemName, value: item.value });
        }
    });

    if (currentCategory.items.length > 0 || categories.length === 0) {
        categories.push(currentCategory);
    }

    return categories;
}

export function ProductSpecBlock({ block, currentStyles, isAdmin, product, viewport }: BlockProps) {
    const productState = useProductState();
    const activeProduct = productState?.product || product || (isAdmin ? DEMO_PRODUCT : null);
    const selectedVariant = productState?.selectedVariant;
    const settings = block.settings || {};

    const [openCategories, setOpenCategories] = useState<Record<string, boolean>>({});
    const [previewSpecs, setPreviewSpecs] = useState<{ name: string; value: string }[] | null>(null);

    const specs = useMemo((): SpecCategory[] | null => {
        let specsSource = (activeProduct && activeProduct.specifications && activeProduct.specifications.length > 0)
            ? activeProduct.specifications
            : null;

        if (isAdmin && previewSpecs) {
            specsSource = previewSpecs;
        }

        if (specsSource && specsSource.length > 0) {
            let mergedSpecs = [...specsSource];

            // Merge variant attributes into matching spec rows
            if (selectedVariant && selectedVariant.attributes) {
                Object.entries(selectedVariant.attributes).forEach(([attrName, attrVal]) => {
                    const attrValStr = String(attrVal);
                    const idx = mergedSpecs.findIndex(
                        (s) => s.name.toLowerCase().trim() === attrName.toLowerCase().trim()
                    );
                    if (idx !== -1) {
                        mergedSpecs[idx] = { ...mergedSpecs[idx], value: attrValStr };
                    } else {
                        mergedSpecs.push({ name: attrName, value: attrValStr });
                    }
                });
            }

            return parseSpecifications(mergedSpecs);
        }

        // Admin canvas: always show a populated preview (demo data) so merchants
        // can see and style the block while building the template. Live
        // storefront: a product with no specifications configured yet should
        // render nothing rather than fake/misleading demo numbers.
        if (isAdmin) {
            return DEFAULT_SPECS_DEMO;
        }
        return null;
    }, [activeProduct, selectedVariant, isAdmin, previewSpecs]);

    const { templates: specTemplates } = useSpecTemplates(isAdmin && !!settings.previewTemplate);
    useEffect(() => {
        setPreviewSpecs(isAdmin && settings.previewTemplate ? specTemplates[settings.previewTemplate] || null : null);
    }, [isAdmin, settings.previewTemplate, specTemplates]);

    const categoriesString = useMemo(() => {
        return specs ? specs.map(s => s.category).join(',') : '';
    }, [specs]);

    useEffect(() => {
        const defaultState = settings.accordionDefaultState || 'all_open';
        const initial: Record<string, boolean> = {};
        if (specs && Array.isArray(specs)) {
            specs.forEach((s, idx) => {
                if (defaultState === 'all_closed') {
                    initial[s.category] = false;
                } else if (defaultState === 'first_open') {
                    initial[s.category] = idx === 0;
                } else { // all_open
                    initial[s.category] = true;
                }
            });
        }
        setOpenCategories(initial);
    }, [settings.accordionDefaultState, categoriesString]);

    const isDarkMode = settings.theme === 'dark';
    const columns: 1 | 2 = settings.columns === 1 ? 1 : 2;
    const showTitle = settings.showTitle !== false;
    const titleText = settings.titleText || 'Specyfikacja Techniczna';
    const titleSubtext = settings.titleSubtext || 'Pełne dane techniczne i parametry użytkowe';
    const accentColor = settings.accentColor || '#ea580c'; // orange-600
    const accentBgColor = settings.accentBgColor || (isDarkMode ? '#1c1917' : '#fff7ed');
    const borderColor = isDarkMode ? '#262626' : '#f5f5f5';
    const dividerColor = isDarkMode ? '#1f1f1f' : '#f5f5f5';

    const toggleCategory = (catName: string) => {
        setOpenCategories((prev) => ({
            ...prev,
            [catName]: !prev[catName],
        }));
    };

    // Default all open
    const isCatOpen = (name: string) => openCategories[name] !== false;

    const sectionStyle = settings.sectionStyle || 'accordion';

    const renderAccordion = (cat: SpecCategory, idx: number, isMobile: boolean) => {
        const isOpen = isCatOpen(cat.category);
        const radius = currentStyles.sectionRadius !== undefined ? Number(currentStyles.sectionRadius) : (settings.sectionRadius !== undefined ? Number(settings.sectionRadius) : 16);

        return (
            <div
                key={idx}
                className="border overflow-hidden transition-all animate-in fade-in duration-200"
                style={{ 
                    borderRadius: radius,
                    borderColor: borderColor
                }}
            >
                <button
                    type="button"
                    onClick={() => toggleCategory(cat.category)}
                    className={cn(
                        "w-full flex justify-between items-center text-left font-bold uppercase tracking-wider transition-all",
                        isMobile ? "px-4 py-3.5 text-xs" : "px-6 py-4 text-sm"
                    )}
                    style={{
                        backgroundColor: settings.headerBgColor || (isDarkMode ? '#1c1917' : '#f8fafc'),
                        borderBottomWidth: isOpen ? 1 : 0,
                        borderColor: borderColor
                    }}
                >
                    <span style={{ color: accentColor }}>{cat.category}</span>
                    {isOpen
                        ? <ChevronUp className="w-4 h-4 opacity-75" style={{ color: accentColor }} />
                        : <ChevronDown className="w-4 h-4 opacity-75" style={{ color: accentColor }} />}
                </button>
                {isOpen && (
                    <div
                        className={cn(
                            "divide-y divide-slate-100/60 bg-white",
                            isMobile ? "px-4 pb-4 pt-2 text-xs" : "px-6 pb-6 pt-3 text-sm"
                        )}
                        style={{ 
                            borderColor: dividerColor,
                            backgroundColor: settings.rowBgColor || (isDarkMode ? '#0a0a0a' : '#ffffff'),
                            color: settings.textColor || 'inherit'
                        }}
                    >
                        {cat.items.map((item, itemIdx) => (
                            <div key={itemIdx} className="flex justify-between py-2.5">
                                <span className="opacity-75 font-medium">{item.name}</span>
                                <span className="font-bold text-right pl-4">{item.value}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const renderFlat = (cat: SpecCategory, idx: number, isMobile: boolean) => {
        const radius = currentStyles.sectionRadius !== undefined ? Number(currentStyles.sectionRadius) : (settings.sectionRadius !== undefined ? Number(settings.sectionRadius) : 16);

        return (
            <div 
                key={idx} 
                className="border p-4 transition-all" 
                style={{ 
                    borderRadius: radius,
                    borderColor: borderColor,
                    backgroundColor: settings.rowBgColor || 'transparent'
                }}
            >
                <div 
                    className={cn("-mx-4 -mt-4 px-4 py-2.5 border-b mb-3 transition-all", isMobile ? "text-xs" : "text-sm")} 
                    style={{ 
                        backgroundColor: settings.headerBgColor || accentBgColor,
                        borderColor: borderColor,
                        borderTopLeftRadius: radius - 1,
                        borderTopRightRadius: radius - 1,
                    }}
                >
                    <h3 className="font-black uppercase tracking-wider" style={{ color: accentColor }}>
                        {cat.category}
                    </h3>
                </div>
                <div className="divide-y divide-slate-100/60">
                    {cat.items.map((item, itemIdx) => (
                        <div
                            key={itemIdx}
                            className={cn("flex justify-between py-2.5", isMobile ? "text-xs" : "text-sm")}
                            style={{ 
                                borderColor: dividerColor,
                                color: settings.textColor || 'inherit'
                            }}
                        >
                            <span className="opacity-75 font-medium">{item.name}</span>
                            <span className="font-bold text-right pl-4">{item.value}</span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const spacing = currentStyles.sectionSpacing !== undefined ? Number(currentStyles.sectionSpacing) : (settings.sectionSpacing !== undefined ? Number(settings.sectionSpacing) : 16);

    // Live storefront, no specifications configured on this product — render nothing
    // instead of the admin-only demo table (see the `specs` memo above).
    if (!specs) return null;

    return (
        <div
            className={cn(
                'transition-all duration-300 border',
                isDarkMode
                    ? 'bg-neutral-950 text-neutral-100 border-neutral-800'
                    : 'bg-white text-neutral-900 border-neutral-100 shadow-sm'
            )}
            style={{ borderRadius: 24, padding: '2.5rem 2rem', ...currentStyles }}
        >
            {isAdmin && <DemoBadge />}
            <div className="max-w-4xl mx-auto space-y-8">
                {showTitle && (
                    <div className={cn('border-b pb-4 flex items-center gap-3', isDarkMode ? 'border-neutral-800' : 'border-neutral-100')}>
                        <div className="p-2 rounded-xl" style={{ backgroundColor: accentBgColor, color: accentColor }}>
                            <Sliders className="w-5 h-5" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold uppercase tracking-wide">{titleText}</h2>
                            {titleSubtext && <p className="text-xs opacity-60">{titleSubtext}</p>}
                        </div>
                    </div>
                )}

                {/* Desktop Layout — admin canvas simulates viewport by scaling a real-width div,
                    so the hidden/md:grid classes below never react to the viewport toggle; the
                    md breakpoint (768px) is crossed by both tablet (768px) and desktop (1440px). */}
                {(isAdmin ? viewport !== 'mobile' : true) && (
                    <div
                        className={cn(!isAdmin && 'hidden md:grid', 'gap-x-12', columns === 1 ? 'grid-cols-1' : 'grid-cols-2')}
                        style={{ rowGap: spacing }}
                    >
                        {specs.map((cat, idx) => (
                            sectionStyle === 'accordion' ? renderAccordion(cat, idx, false) : renderFlat(cat, idx, false)
                        ))}
                    </div>
                )}

                {/* Mobile Layout */}
                {(isAdmin ? viewport === 'mobile' : true) && (
                    <div
                        className={cn(!isAdmin && 'block md:hidden', 'flex flex-col')}
                        style={{ gap: spacing }}
                    >
                        {specs.map((cat, idx) => (
                            sectionStyle === 'accordion' ? renderAccordion(cat, idx, true) : renderFlat(cat, idx, true)
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export const PRODUCT_BLOCK_TYPES = new Set([
    'product-name',
    'product-price',
    'product-quantity',
    'product-gallery',
    'product-description-short',
    'product-description-long',
    'product-feature-bullets',
    'product-variants',
    'product-add-to-cart',
    'product-stock',
    'product-reviews',
    'product-rating-mini',
    'product-condition',
    'product-spec',
    'product-downloads',
    'product-leasenow-button',
    'product-leasenow-calculator',
]);

export function ProductBlocks(props: BlockProps) {
    switch (props.block.type) {
        case 'product-name':              return <ProductNameBlock {...props} />;
        case 'product-price':             return <ProductPriceBlock {...props} />;
        case 'product-quantity':          return <ProductQuantityBlock {...props} />;
        case 'product-gallery':           return <ProductGalleryBlock {...props} />;
        case 'product-description-short': return <ProductDescriptionShortBlock {...props} />;
        case 'product-description-long':  return <ProductDescriptionLongBlock {...props} />;
        case 'product-feature-bullets':   return <ProductFeatureBulletsBlock {...props} />;
        case 'product-variants':          return <ProductVariantsBlock {...props} />;
        case 'product-add-to-cart':       return <ProductAddToCartBlock {...props} />;
        case 'product-stock':             return <ProductStockBlock {...props} />;
        case 'product-reviews':           return <ProductReviewsBlock {...props} />;
        case 'product-rating-mini':       return <ProductRatingMiniBlock {...props} />;
        case 'product-condition':         return <ProductConditionBlock {...props} />;
        case 'product-spec':              return <ProductSpecBlock {...props} />;
        case 'product-downloads':         return <ProductDownloadsBlock {...props} />;
        case 'product-leasenow-button':   return <LeaseNowButtonBlock {...props} />;
        case 'product-leasenow-calculator': return <LeaseNowCalculatorBlock {...props} />;
        default:                          return null;
    }
}
